import { useEffect, useMemo, useRef, useState } from 'react';
import * as Sentry from "@sentry/react";
import { addDoc, setDoc, arrayUnion, collection, doc, onSnapshot, orderBy, query, serverTimestamp, updateDoc, deleteDoc, getDoc, getDocs, where, increment, writeBatch } from 'firebase/firestore';
import {
  SUMMARY_TIMEOUT_MS,
  INSIGHT_TIMEOUT_MS,
  CHAT_OUTPUT_TOKENS,
  DEEP_DISCUSS_OUTPUT_TOKENS,
  CONTEXT_SELECT_OUTPUT_TOKENS,
  SUMMARY_OUTPUT_TOKENS,
  INSIGHT_OUTPUT_TOKENS,
  MAX_MESSAGES_PER_ROOM,
  MESSAGE_WARNING_THRESHOLD,
  INPUT_MEMO_SUGGESTION_INTERVAL,
  LONG_PASTE_CHAR_THRESHOLD,
  SINK_INPUT_CHAR_LIMIT,
  AI_ACTIVE_INPUT_CHAR_LIMIT,
  PROB_INPUT_MEMO_SUGGESTION_INTERVAL,
  IS_MOCK_DEMO,
  ASSET_TYPE,
  ASSET_STATUS,
  ASSET_MATURITY,
  NOTE_STATUS,
  INSIGHT_STATUS,
  INSIGHT_TYPE,
  CHAT_ROOM_STATUS,
  CHAT_MESSAGE_KIND,
  ASSET_TYPES,
  ASSET_TYPE_DESCRIPTIONS,
  ASSET_TYPE_LABELS,
  ASSET_STATUS_LABELS,
  ACTION_TYPE,
  ERROR_FINISH_REASON,
  INPUT_MODE_DETAILS,
  SETTINGS_KEYS,
  LIGHT_ACTIONS,
  HEAVY_ACTIONS,
  CREDIT_UX_PATTERN,
  CREDIT_WARNING_THRESHOLD,
  MAX_TITLE_LENGTH,
} from './constants';
import {
  readSetting,
  isUserMessage,
  canViewSharedAssets,
  canDiscussSharedAssetRef,
  createMockId,
  extractJsonObject,
  normalizeAssetType,
  buildFallbackInsight,
  getAssetTypeLabel,
  getAssetStatusLabel,
  getInitialAssetStatus,
  mapErrorToUserMessage,
  sanitizeTags,
  isProposedChangeInsight,
} from './lib/utils';
import { getProviderModel, isNormalFinishReason, generateText } from './lib/llm/api';
import { getSimilarity } from './lib/health';
import {
  buildMemoSuggestionText,
  buildAssetSelectionPrompt,
  buildThinkingPrompt,
  buildSummaryPrompt,
  buildInsightPrompt,
  buildTagInsightPrompt,
  buildHealthCheckPrompt,
} from './prompts';
import { buildMockReply, buildMockSummary, buildMockInsightCandidates, buildMockHealthProposals } from './lib/mockUtils';
import '@xyflow/react/dist/style.css';
import { db } from './firebase';
import { mockChatRooms, mockMessages } from './mockData';

import { useNotes } from './hooks/useNotes';
import { useInsights } from './hooks/useInsights';
import { useAssets } from './hooks/useAssets';
import { useTags } from './hooks/useTags';
import { useSyncGraph } from './hooks/useSyncGraph';
import { syncNormalizedTags } from './lib/tags';
import { detectDuplicateNotes, detectDuplicateAssets, detectDuplicateInsights, detectTagMergeCandidates } from './lib/health';
import { trackEvent } from './lib/analytics.js';
import { isOfflineError } from './lib/firestore';
import { NOTE_STATUS_FILTER_OPTIONS, INSIGHT_STATUS_FILTER_OPTIONS, ASSET_STATUS_FILTER_OPTIONS } from './lib/statusFilters';
import { PROB_REPORT_BUDDY_INSTRUCTIONS, formatProbReportForBuddy, isProbReportSharedAsset, isValidSharedAssetId } from './lib/sharedAssets';
import { getRoomWorkflowState } from './lib/roomWorkflow';
import { ASSET_USAGE_RULE_VERSION, ASSET_USAGE_STATUS, deriveAssetUsageMetrics, deriveAssetUsageStatus, isArchiveCandidate } from './lib/assetUsage';

import CreditLimitModal from './components/CreditLimitModal';
import NoteList from './components/notes/NoteList';
import NoteDetail from './components/notes/NoteDetail';
import InsightList from './components/insights/InsightList';
import InsightDetail from './components/insights/InsightDetail';
import AssetList from './components/assets/AssetList';
import AssetDetail from './components/assets/AssetDetail';
import KnowledgeHealthPanel from './components/insights/KnowledgeHealthPanel';
import SinkChatPane from './components/chat/SinkChatPane';
import ThinkGraph from './components/think/ThinkGraph';
import ThinkList from './components/think/ThinkList';
import SettingsPage from './components/SettingsPage';
import SettingsModal from './components/modals/SettingsModal';
import RejectModal from './components/modals/RejectModal';
import MemoModal from './components/modals/MemoModal';
import NoteCreateModal from './components/modals/NoteCreateModal';
import AssetTypeModal from './components/modals/AssetTypeModal';
import ChatListModal from './components/modals/ChatListModal';
import NewChatConfirmModal from './components/modals/NewChatConfirmModal';
import Guide from './components/Guide';
import Toast from './components/Toast';
import { Panel, Group as PanelGroup, Separator as PanelResizeHandle } from 'react-resizable-panels';

/**
 * @typedef {Object} ContextRef
 * @property {"note" | "insight" | "concept" | "tag" | "sharedAsset"} type
 * @property {string} id
 * @property {string} [sourceApp]
 */

/**
 * @typedef {Object} ChatRoom
 * @property {string} id
 * @property {string} title
 * @property {"input" | "discuss" | "anti"} mode
 * @property {"active" | "summarized" | "assetized" | "archived"} status
 * @property {string | null} summaryNoteId
 * @property {ContextRef[]} [contextRefs]
 * @property {string} createdAt
 * @property {string} updatedAt
 * @property {string} lastMessageAt
 * @property {string} [ownerUid]
 */

export default function App({ user, signOut, surface }) {
  const [viewportHeight, setViewportHeight] = useState('100dvh');
  const [viewportTop, setViewportTop] = useState(0);
  const stableMobileViewportHeightRef = useRef(null);

  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [isStandalonePWA, setIsStandalonePWA] = useState(false);

  useEffect(() => {
    document.body.classList.add('app-viewport-lock');
    return () => {
      document.body.classList.remove('app-viewport-lock');
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.visualViewport) {
        const isLargeNow = window.innerWidth >= 1024;
        const isStandalone = window.navigator.standalone === true || window.matchMedia('(display-mode: standalone)').matches;
        const activeElement = document.activeElement;
        const hasFocusedTextInput = activeElement?.matches?.('input, textarea, [contenteditable="true"]');
        const visualViewportHeight = Math.round(window.visualViewport.height || window.innerHeight);
        const layoutViewportHeight = Math.round(window.innerHeight || visualViewportHeight);

        if (isLargeNow) {
          stableMobileViewportHeightRef.current = null;
        } else if (!hasFocusedTextInput) {
          stableMobileViewportHeightRef.current = Math.max(layoutViewportHeight, visualViewportHeight);
        }

        const stableMobileViewportHeight = stableMobileViewportHeightRef.current ?? Math.max(layoutViewportHeight, visualViewportHeight);
        const viewportHeightDelta = layoutViewportHeight - visualViewportHeight;
        const viewportShrinkFromStable = stableMobileViewportHeight - Math.min(layoutViewportHeight, visualViewportHeight);
        const keyboardOpen = !isLargeNow && hasFocusedTextInput && (viewportHeightDelta > 120 || viewportShrinkFromStable > 120);

        // Proactively reset layout viewport on iOS when keyboard is not active
        // but the layout viewport has been shifted (window.scrollY > 0).
        if (!keyboardOpen && window.scrollY > 0 && !isLargeNow) {
          window.scrollTo(0, 0);
        }

        const nextViewportTop = Math.max(0, Math.round(window.visualViewport.offsetTop || 0));

        setIsStandalonePWA(isStandalone);
        setIsKeyboardOpen(keyboardOpen);
        setViewportTop(isLargeNow ? 0 : (keyboardOpen ? nextViewportTop : 0));
        if (!isLargeNow) {
          // Browser chrome can shrink visualViewport even when the keyboard is closed.
          // Use innerHeight for the stable app frame, then switch to the smaller viewport while editing.
          const mobileViewportHeight = keyboardOpen ? Math.min(layoutViewportHeight, visualViewportHeight) : layoutViewportHeight;
          setViewportHeight(`${mobileViewportHeight}px`);
        }
      }
    };
    const handleFocusChange = () => {
      window.setTimeout(handleResize, 0);
    };
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
      window.visualViewport.addEventListener('scroll', handleResize);
      window.addEventListener('resize', handleResize);
      window.addEventListener('focusin', handleFocusChange);
      window.addEventListener('focusout', handleFocusChange);
      handleResize();
    }
    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
        window.visualViewport.removeEventListener('scroll', handleResize);
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('focusin', handleFocusChange);
        window.removeEventListener('focusout', handleFocusChange);
      }
    };
  }, []);

  const normalizedTags = useTags(user);
  const tagSuggestions = useMemo(() => normalizedTags.map(t => t.name), [normalizedTags]);
  const [globalSelectedTags, setGlobalSelectedTags] = useState([]);

  const {
    insights,
    setInsights,
    insightSearchText,
    setInsightSearchText,
    selectedInsightStatus,
    setSelectedInsightStatus,
    selectedInsightSourceNoteId,
    setSelectedInsightSourceNoteId,
    selectedInsightSourceIds,
    setSelectedInsightSourceIds,
    selectedInsightKind,
    setSelectedInsightKind,
    selectedInsightType,
    setSelectedInsightType,
    setSelectedInsightId,
    selectedInsight,
    pendingHintCount,
    filteredInsights,
    isRejectModalOpen,
    setIsRejectModalOpen,
    rejectingInsightId,
    setRejectingInsightId,
    rejectReason,
    setRejectReason,
    isAssetTypeModalOpen,
    setIsAssetTypeModalOpen,
    assetizingInsight,
    setAssetizingInsight,
    assetTypeDraft,
    setAssetTypeDraft,
    assetTitleDraft,
    setAssetTitleDraft,
    assetBodyDraft,
    setAssetBodyDraft,
    handleUpdateInsightTags,
    handleUpdateInsightTitle,
    handleUpdateInsightSummary,
    handleRejectInsight,
    handleUpdateInsightMemo,
    handleUpdateInsightPatch,
    openAssetTypeModal,
  } = useInsights(user, globalSelectedTags);

  const {
    notes,
    setNotes,
    setSelectedNoteId,
    noteSearchText,
    setNoteSearchText,
    selectedNoteFilter,
    setSelectedNoteFilter,
    selectedNote,
    filteredNotes,
    notesForSelectedTag,
    handleUpdateNoteTags,
    handleUpdateNoteBody,
    handleCreateNote,
    handleUpdateNoteStatus,
  } = useNotes(user, globalSelectedTags, insights);

  const {
    concepts,
    setConcepts,
    setSelectedAssetId,
    selectedAssetStatus,
    setSelectedAssetStatus,
    assetSearchText,
    setAssetSearchText,
    selectedAssetFilter,
    setSelectedAssetFilter,
    selectedAsset,
    filteredAssets,
    handleUpdateAssetMaturity,
    handleUpdateAssetStatus,
    handleUpdateAssetTitle,
    handleUpdateAssetBody,
    handleUpdateAssetType,
    handleUpdateAssetTags,
  } = useAssets(user, globalSelectedTags);

  const [isLargeScreen, setIsLargeScreen] = useState(typeof window !== 'undefined' && window.innerWidth >= 1024);
  const [showPWAGuide, setShowPWAGuide] = useState(false);

  useEffect(() => {
    // Detect iOS Safari in non-standalone mode
    const checkPWAStatus = () => {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      const isStandalone = window.navigator.standalone === true || window.matchMedia('(display-mode: standalone)').matches;
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      const isDismissed = localStorage.getItem('sink.pwaGuideDismissed') === 'true';

      if (isIOS && isSafari && !isStandalone && !isDismissed) {
        setShowPWAGuide(true);
      }
    };
    checkPWAStatus();
  }, []);

  const [mapFilterMode, setMapFilterMode] = useState('important'); // 'important', 'recent', 'unprocessed'
  const [mapSearchQuery, setMapSearchQuery] = useState('');

  const {
    syncHistory,
    setSyncHistory,
    syncNodeTypes,
    syncGraph,
    syncCurrentId,
    syncCurrentNode,
    selectedSyncNode,
    syncFlowNodes,
    syncFlowEdges,
    syncTags,
    syncTagsCount,
    handleSyncNodeClick,
    handleDrillDown,
    navigateSyncHistory,
    handleJumpToMap,
    setSelectedSyncNodeId,
  } = useSyncGraph({ notes, insights, concepts, normalizedTags, mapFilterMode, mapSearchQuery, isLargeScreen, globalSelectedTags });

  const handleResetThink = () => {
    setSyncHistory([{ id: 'root', label: 'Think' }]);
    setMapFilterMode('important');
    setMapSearchQuery('');
    setGlobalSelectedTags([]);
    setSelectedSyncNodeId(null);
  };

  const jumpToMap = (nodeId, label) => {
    handleJumpToMap(nodeId, label);
    setSyncViewMode('map');
    setActiveTab('think');
  };

  useEffect(() => {
    const handleResize = () => setIsLargeScreen(window.innerWidth >= 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [activeTab, setActiveTab] = useState('input');

  useEffect(() => {
    const eventName = surface === 'demo' ? 'demo_tab_open' : 'app_tab_open';
    trackEvent(eventName, { surface, tab: activeTab });
  }, [activeTab, surface]);

  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
  };

  const handleResend = async (errorMessage) => {
    if (!errorMessage.metadata?.retryData) return;

    const { text, mode } = errorMessage.metadata.retryData;

    // Remove the error message from local state
    setLocalMessages(prev => prev.filter(m => m.id !== errorMessage.id));

    // Restore the input text if it's currently empty (optional but helpful)
    if (!sinkInputText) setSinkInputText(text);

    // Call handleSave with the original data
    // We need to simulate the event or pass null
    await handleSave(null, mode, text);
  };
  const [sinkViewMode, setSinkViewMode] = useState('input');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const action = params.get('action');
    if (action === 'new-memo') {
      setTimeout(() => {
        setActiveTab('input');
        setSinkViewMode('input');
      }, 0);
      // Clear the parameter so it doesn't re-trigger on reload
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, []);
  const [inputMode, setInputMode] = useState('input');
  const [sinkInputText, setSinkInputText] = useState('');
  const [sinkDraftMetadata, setSinkDraftMetadata] = useState(null);
  const [historyIndex, setHistoryIndex] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSummarizing, setIsSummarizing] = useState(false);
  const [sinkRoomId, setSinkRoomId] = useState(() => (IS_MOCK_DEMO ? 'mock-room-sink' : readSetting(`secondBrain.${user?.uid}.sinkRoomId`)));
  const [ignoredAssetIdsByRoom, setIgnoredAssetIdsByRoom] = useState({});

  const [isNewChatConfirmOpen, setIsNewChatConfirmOpen] = useState(false);
  const [newChatContextRefs, setNewChatContextRefs] = useState([]);
  const [newChatInitialTitle, setNewChatInitialTitle] = useState(null);
  const [newChatInitialMode, setNewChatInitialMode] = useState(null);
  const [pendingSinkContextRefs, setPendingSinkContextRefs] = useState([]);
  const [pendingSinkRoomTitle, setPendingSinkRoomTitle] = useState(null);
  const [pendingSharedAssetRef, setPendingSharedAssetRef] = useState(null);

  const handleSinkInputTextChange = (value) => {
    setSinkInputText(value);
    if (value.trim().length === 0) {
      setSinkDraftMetadata(null);
    }
  };

  const handleLongPasteDetected = ({ charCount }) => {
    setSinkDraftMetadata({
      inputKind: 'paste',
      captureType: 'external_clip',
      sourceType: 'unknown',
      pastedCharCount: charCount,
      detectedAt: new Date().toISOString(),
    });
    showToast('長文ペーストを外部クリップとして保存します。', 'info');
  };

  const [chatRooms, setChatRooms] = useState(() => (IS_MOCK_DEMO ? mockChatRooms : []));
  const [isRoomsLoaded, setIsRoomsLoaded] = useState(IS_MOCK_DEMO);
  const notesById = useMemo(() => new Map(notes.map((note) => [note.id, note])), [notes]);
  const chatRoomsWithWorkflow = useMemo(() => (
    chatRooms.map((room) => {
      // Notes may load slightly after rooms; until then we still surface a stable
      // summarized state and upgrade to "Noteまとめ後に更新あり" once note timestamps arrive.
      const summaryNote = room.summaryNoteId ? notesById.get(room.summaryNoteId) || null : null;
      return {
        ...room,
        workflow: getRoomWorkflowState(room, summaryNote),
      };
    })
  ), [chatRooms, notesById]);
  const currentRoom = useMemo(() => chatRoomsWithWorkflow.find(r => r.id === sinkRoomId) || null, [chatRoomsWithWorkflow, sinkRoomId]);

  const existingSummaryNote = useMemo(() => {
    const room = chatRoomsWithWorkflow.find(r => r.id === sinkRoomId);
    if (!room?.summaryNoteId) return null;
    return notes.find(n => n.id === room.summaryNoteId) || null;
  }, [chatRoomsWithWorkflow, sinkRoomId, notes]);

  const assetDuplicateCandidates = useMemo(() => {
    if (!assetizingInsight) return [];
    const searchTitle = assetTitleDraft || assetizingInsight.title;
    return concepts.filter(asset => {
      if (asset.status === ASSET_STATUS.ARCHIVED) return false;
      const titleSim = getSimilarity(searchTitle, asset.title, 0.8);
      return titleSim > 0.8;
    }).slice(0, 3);
  }, [assetizingInsight, assetTitleDraft, concepts]);
  const [messages, setMessages] = useState(() => (IS_MOCK_DEMO ? mockMessages : []));
  const [localMessages, setLocalMessages] = useState([]);
  const [userData, setUserData] = useState(() => (IS_MOCK_DEMO ? {
    grantedCredits: 1000,
    usedCredits: 400,
    resetAt: '2026-06-01',
    role: 'credit_beta',
    plan: 'standard',
    featureFlags: {
      canViewSharedAssets: true,
      canDiscussSharedAssets: true,
      canViewProb: true
    }
  } : null));

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sinkAction = params.get('sinkAction');
    const sharedAssetId = params.get('sharedAssetId')?.trim();
    const section = params.get('section')?.trim();
    const sectionQuote = params.get('sectionQuote')?.trim();

    if (!isRoomsLoaded || !['discuss', 'input'].includes(sinkAction) || !sharedAssetId) return;

    window.history.replaceState({}, '', window.location.pathname);

    setTimeout(() => {
      if (!isValidSharedAssetId(sharedAssetId)) {
        setToast({ message: 'ProbレポートIDが不正です。', type: 'warning' });
        return;
      }

      const contextRef = { type: 'sharedAsset', id: sharedAssetId, sourceApp: 'prob' };

      if (sinkAction === 'input') {
        const existingRoom = chatRooms.find(r =>
          r.mode === 'input' &&
          r.contextRefs?.some(ref => ref.type === 'sharedAsset' && ref.id === sharedAssetId)
        );

        if (existingRoom) {
          setSinkRoomId(existingRoom.id);
          localStorage.setItem(`secondBrain.${user?.uid}.sinkRoomId`, existingRoom.id);
        } else {
          setPendingSinkContextRefs([contextRef]);
          setPendingSinkRoomTitle('Probレポートのメモ');
          setSinkRoomId('');
        }

        if (section) {
          setSinkInputText(`【${section}】 `);
        }

        setPendingSharedAssetRef({ id: sharedAssetId, section, sectionQuote });
        setActiveTab('input');
        setSinkViewMode('input');
        setInputMode('input');
      } else {
        setNewChatContextRefs([contextRef]);
        setNewChatInitialTitle('Probレポートについて議論');
        setNewChatInitialMode('discuss');
        setIsNewChatConfirmOpen(true);
        setActiveTab('input');
        setSinkViewMode('input');
        setInputMode('discuss');
      }
    }, 0);
  }, [chatRooms, user?.uid, isRoomsLoaded]);

  // Collections for Product Task 06 (Links and Normalized Tags)
  // eslint-disable-next-line no-unused-vars
  const [links, setLinks] = useState([]);

  const [assetViewMode, setAssetViewMode] = useState('insights');
  const [highlightedRawMessageIds, setHighlightedRawMessageIds] = useState([]);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const openSettings = () => {
    closeAllModals();
    if (isLargeScreen) {
      setIsSettingsModalOpen(true);
    } else {
      setActiveTab('settings');
    }
  };
  const [isSyncTrayExpanded, setIsSyncTrayExpanded] = useState(false);
  const [isInsightStatusFilterOpen, setIsInsightStatusFilterOpen] = useState(false);
  const [isAssetStatusFilterOpen, setIsAssetStatusFilterOpen] = useState(false);
  const [isNoteStatusFilterOpen, setIsNoteStatusFilterOpen] = useState(false);
  const [isDeepDiscussMode, setIsDeepDiscussMode] = useState(false);
  const [isMultilineMode, setIsMultilineMode] = useState(false);
  
  const [isMemoModalOpen, setIsMemoModalOpen] = useState(false);
  const [isNoteCreateModalOpen, setIsNoteCreateModalOpen] = useState(false);
  const [isChatListModalOpen, setIsChatListModalOpen] = useState(false);
  const [chatListStatusFilter, setChatListStatusFilter] = useState(null);

  const openChatHistory = () => {
    setChatListStatusFilter(null);
    setIsChatListModalOpen(true);
  };

  const openChatHistoryPending = () => {
    setChatListStatusFilter('pending_summary');
    setIsChatListModalOpen(true);
  };
  const [syncViewMode, setSyncViewMode] = useState('map'); // 'map', or 'list'
  const [isGuideOpen, setIsGuideOpen] = useState(surface === 'demo');

  const [guideStepIndex, setGuideStepIndex] = useState(0);
  const [isCrystallizing, setIsCrystallizing] = useState(false);
  const [isAuditing, setIsAuditing] = useState(false);
  const [isAcceptingProposal, setIsAcceptingProposal] = useState(false);
  const [isHealthChecking, setIsHealthChecking] = useState(false);
  const [isHealthPanelExpanded, setIsHealthPanelExpanded] = useState(false);

  const handleToggleSyncTray = () => {
    setIsSyncTrayExpanded((current) => {
      const next = !current;
      if (!next) {
        setIsHealthPanelExpanded(false);
      }
      return next;
    });
  };

  const handleToggleHealthPanel = (e) => {
    e.stopPropagation();
    setIsSyncTrayExpanded(true);
    setIsHealthPanelExpanded((current) => !current);
  };
  const [isProcessingDuplicate, setIsProcessingDuplicate] = useState(false);
  const [memoSaveMode, setMemoSaveMode] = useState('new');
  const [memoHint, setMemoHint] = useState('');
  const [llmProvider, setLlmProvider] = useState(() => readSetting(SETTINGS_KEYS.provider, 'gemini'));
  const [llmModel, setLlmModel] = useState(() => readSetting(SETTINGS_KEYS.model, ''));
  const [llmModelLight, setLlmModelLight] = useState(() => readSetting(SETTINGS_KEYS.modelLight, ''));
  const [llmModelHeavy, setLlmModelHeavy] = useState(() => readSetting(SETTINGS_KEYS.modelHeavy, ''));
  const [openaiApiKey, setOpenaiApiKey] = useState(() => readSetting(SETTINGS_KEYS.openaiKey));
  const [anthropicApiKey, setAnthropicApiKey] = useState(() => readSetting(SETTINGS_KEYS.anthropicKey));
  const [geminiApiKey, setGeminiApiKey] = useState(() => readSetting(SETTINGS_KEYS.geminiKey));
  const [debugMode, setDebugMode] = useState(() => readSetting(SETTINGS_KEYS.debug) === 'true');

  const isByok = useMemo(() => !!(openaiApiKey?.trim() || anthropicApiKey?.trim() || geminiApiKey?.trim()), [openaiApiKey, anthropicApiKey, geminiApiKey]);

  useEffect(() => {
    if (surface !== 'app' || !user?.uid || !userData) return;

    if (userData.role === 'owner') {
      localStorage.setItem('secondBrain.analyticsOptOut', 'true');
      return;
    }
    // owner 以外でログインした場合はフラグを解除
    localStorage.removeItem('secondBrain.analyticsOptOut');

    trackEvent('app_open', {
      surface,
      role: userData.role,
      credit_bucket: userData.usedCredits < userData.grantedCredits ? 'under' : 'over',
      is_byok: isByok
    });
  }, [surface, user?.uid, userData, isByok]);

  const checkAiAvailability = () => {
    if (IS_MOCK_DEMO) return true;

    // Check if current provider has a key
    const currentKey = getCurrentApiKey();
    if (currentKey) return true;

    // If no local key, check if user has credits and is logged in (proxy fallback)
    if (user && (userData?.role === 'credit_beta' || userData?.role === 'owner')) {
      return true;
    }

    return false;
  };

  const [isCreditLimitModalOpen, setIsCreditLimitModalOpen] = useState(false);
  const [creditLimitMessage, setCreditLimitMessage] = useState('');

  const closeAllModals = () => {
    setIsSettingsModalOpen(false);
    setIsChatListModalOpen(false);
    setIsMemoModalOpen(false);
    setIsNoteCreateModalOpen(false);
    setIsNewChatConfirmOpen(false);
    setIsCreditLimitModalOpen(false);
    setIsRejectModalOpen(false);
    setIsAssetTypeModalOpen(false);
  };

  const handleRequestCredits = async () => {
    if (IS_MOCK_DEMO || !user?.uid) return;
    try {
      await setDoc(doc(db, 'creditRequests', user.uid), {
        ownerUid: user.uid,
        email: user.email,
        status: 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Failed to request credits:', error);
      Sentry.captureException(error, { tags: { action: 'handleRequestCredits' } });
      alert('クレジット申請に失敗しました。');
    }
  };

  const sinkMessagesEndRef = useRef(null);
  const sinkScrollContainerRef = useRef(null);
  const lastCheckedSinkRoomId = useRef(null);
  const sinkRoomScrollPositions = useRef({});
  const lastActiveRoomId = useRef(null);
  const lastSinkViewMode = useRef(null);
  const lastInputMode = useRef(inputMode);

  const userInputHistory = useMemo(() => (
    messages
      .filter((message) => (
        message.roomId === sinkRoomId
        && isUserMessage(message)
        && (message.body ?? message.text)?.trim()
      ))
      .map((message) => message.body ?? message.text)
  ), [messages, sinkRoomId]);

  const selectedNoteInsightCount = useMemo(() => (
    selectedNote
      ? insights.filter((insight) => (insight.sourceNoteIds || []).includes(selectedNote.id)).length
      : 0
  ), [insights, selectedNote]);

  const allAvailableTags = useMemo(() => {
    // Optimized: If normalizedTags collection is available, use it directly as the primary source.
    // This avoids O(N) scan of all documents (notes, insights, assets) which can be expensive for large data sets.
    if (normalizedTags.length > 0) {
      return [...normalizedTags]
        .sort((a, b) => {
          const timeA = a.lastUsedAt ? (typeof a.lastUsedAt.toMillis === 'function' ? a.lastUsedAt.toMillis() : new Date(a.lastUsedAt).getTime()) : 0;
          const timeB = b.lastUsedAt ? (typeof b.lastUsedAt.toMillis === 'function' ? b.lastUsedAt.toMillis() : new Date(b.lastUsedAt).getTime()) : 0;
          return timeB - timeA || a.name.localeCompare(b.name, 'ja');
        })
        .map(t => t.name);
    }

    // Fallback: Collect all tags from documents to handle cases where normalizedTags is not yet loaded or empty.
    const tagMap = new Map();
    const processItems = (items) => {
      items.forEach(item => {
        const timestamp = item.updatedAt || item.createdAt;
        const ms = timestamp
          ? (typeof timestamp.toMillis === 'function' ? timestamp.toMillis() : new Date(timestamp).getTime())
          : 0;
        (item.tags || []).forEach(tag => {
          if (!tagMap.has(tag) || ms > tagMap.get(tag)) {
            tagMap.set(tag, ms);
          }
        });
      });
    };

    processItems(notes);
    processItems(insights);
    processItems(concepts);

    return Array.from(tagMap.keys()).sort((a, b) => {
      const timeA = tagMap.get(a) || 0;
      const timeB = tagMap.get(b) || 0;
      if (timeA !== timeB) return timeB - timeA;
      return a.localeCompare(b, 'ja');
    });
  }, [notes, insights, concepts, normalizedTags]);

  const demoSteps = [
    {
      id: 'sink',
      label: 'Sink',
      guideTitle: 'Sinkで断片を入れ、Noteにまとめる',
      guideBody: 'アイデアやAI回答の抜粋をMemoとして投げ込み、Noteにまとめます。',
      guideAction: 'Sinkを開く',
      guideChecklist: ['Memoを入れる', 'Noteまとめする', 'Noteを確認する'],
      count: messages.length + notes.length,
      state: activeTab === 'input' ? 'active' : notes.length > 0 ? 'done' : messages.length > 0 ? 'done' : 'todo',
      action: openSinkInput,
    },
    {
      id: 'sync',
      label: 'Sync整理',
      guideTitle: 'Syncで思考の手がかりを整理',
      guideBody: 'Noteから抽出したHintを確認し、必要なものだけToDo / Project / Assetとして残します。',
      guideAction: 'Syncを開く',
      guideChecklist: ['Hint候補を確認する', 'Noteのまま残すか決める', '必要なものだけ分類する'],
      count: insights.length + concepts.length,
      state: activeTab === 'sync' ? 'active' : concepts.length > 0 ? 'done' : insights.length > 0 ? 'done' : 'todo',
      action: () => {
        setSelectedAssetId(null);
        openSyncWorkspace('insights');
        setIsSyncTrayExpanded(true);
      },
    },
    {
      id: 'think',
      label: 'Think探索',
      guideTitle: 'Thinkで知識を広げる',
      guideBody: 'Assetの繋がりを俯瞰し、AIと壁打ちします。タグを開いて関連Assetを見ましょう。',
      guideAction: 'Thinkを開く',
      guideChecklist: ['繋がりを見る', 'Assets/Noteを開く', 'AIと壁打ちする'],
      count: syncGraph.nodesById.size,
      state: activeTab === 'think' ? 'active' : syncGraph.nodesById.size > 1 ? 'done' : 'todo',
      action: openThinkWorkspace,
    },
  ];
  const currentGuideStep = demoSteps[guideStepIndex] || demoSteps[0];
  const nextGuideStep = demoSteps[Math.min(guideStepIndex + 1, demoSteps.length - 1)];

  const sinkMessages = useMemo(() => {
    const fromDb = messages.filter(m => m.roomId === sinkRoomId);
    const fromLocal = localMessages.filter(m => m.roomId === sinkRoomId);
    return [...fromDb, ...fromLocal].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  }, [messages, localMessages, sinkRoomId]);

  const lastAssistantMessage = useMemo(() => {
    return [...sinkMessages].reverse().find(m => {
      const source = m.source ?? m.role;
      return (source === 'assistant' || source === 'buddy') && m.kind === CHAT_MESSAGE_KIND.CHAT_REPLY;
    });
  }, [sinkMessages]);

  const activeReferencedAssets = useMemo(() => {
    const ignoredIds = ignoredAssetIdsByRoom[sinkRoomId] || [];
    const ids = lastAssistantMessage?.metadata?.referencedAssetIds || [];
    return ids
      .filter(id => !ignoredIds.includes(id))
      .map(id => concepts.find(c => c.id === id))
      .filter(Boolean)
      .slice(0, 3);
  }, [lastAssistantMessage, ignoredAssetIdsByRoom, sinkRoomId, concepts]);

  const handleRemoveIgnoredAsset = (assetId) => {
    setIgnoredAssetIdsByRoom(prev => ({
      ...prev,
      [sinkRoomId]: [...new Set([...(prev[sinkRoomId] || []), assetId])],
    }));
  };

  const isSinkRoomAtLimit = (inputMode === 'discuss' || inputMode === 'anti') && sinkMessages.length >= MAX_MESSAGES_PER_ROOM;
  const isSinkRoomNearLimit = (inputMode === 'discuss' || inputMode === 'anti') && sinkMessages.length >= MESSAGE_WARNING_THRESHOLD && sinkMessages.length < MAX_MESSAGES_PER_ROOM;

  // users subscription
  useEffect(() => {
    if (IS_MOCK_DEMO || !user?.uid) return undefined;

    return onSnapshot(doc(db, 'users', user.uid), (snapshot) => {
      if (snapshot.exists()) {
        setUserData(snapshot.data());
      } else {
        setUserData(null);
      }
    }, (error) => {
      console.error('Error fetching user limits:', error);
      Sentry.captureException(error, { tags: { action: 'subscribe_users' } });
    });
  }, [user?.uid]);

  // Sync links
  useEffect(() => {
    if (IS_MOCK_DEMO || !user?.uid) return undefined;
    const linksQuery = query(
      collection(db, 'links'),
      where('ownerUid', '==', user.uid),
      orderBy('createdAt', 'desc')
    );

    return onSnapshot(linksQuery, (snapshot) => {
      setLinks(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      console.error('Error fetching links:', error);
      Sentry.captureException(error, { tags: { action: 'subscribe_links' } });
    });
  }, [user?.uid]);

  useEffect(() => {
    if (IS_MOCK_DEMO || !user?.uid) return undefined;
    const roomsQuery = query(
      collection(db, 'chatRooms'),
      where('ownerUid', '==', user.uid),
      orderBy('createdAt', 'desc')
    );

    return onSnapshot(roomsQuery, (snapshot) => {
      setChatRooms(snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })));
      setIsRoomsLoaded(true);
    }, (error) => {
      console.error('Failed to subscribe chat rooms:', error);
    });
  }, [user?.uid]);

  useEffect(() => {
    if (IS_MOCK_DEMO || !user?.uid) return undefined;
    const messagesQuery = query(
      collection(db, 'chatMessages'),
      where('ownerUid', '==', user.uid),
      orderBy('createdAt', 'asc')
    );

    return onSnapshot(messagesQuery, (snapshot) => {
      setMessages(snapshot.docs
        .map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
        .filter((message) => 
          (sinkRoomId && message.roomId === sinkRoomId)
        ));
    }, (error) => {
      console.error('Failed to subscribe chat messages:', error);
    });
  }, [sinkRoomId, user?.uid]);

  const scrollToBottom = (endRef, containerRef, options = {}) => {
    const { force = false, behavior = 'smooth' } = options;
    if (!endRef.current) return;

    const shouldScroll = force || (() => {
      if (!containerRef.current) return true;
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      return scrollHeight - scrollTop <= clientHeight + 150;
    })();

    if (shouldScroll) {
      endRef.current.scrollIntoView({ behavior, block: 'end' });
    }
  };

  useEffect(() => {
    if (sinkViewMode !== 'input') {
      lastSinkViewMode.current = sinkViewMode;
      return;
    }

    const roomChanged = lastActiveRoomId.current !== sinkRoomId;
    const viewChanged = lastSinkViewMode.current !== sinkViewMode;
    const modeChanged = lastInputMode.current !== inputMode;

    if (roomChanged || viewChanged || modeChanged) {
      const savedPos = sinkRoomScrollPositions.current[sinkRoomId];
      if (savedPos !== undefined && sinkScrollContainerRef.current && !modeChanged) {
        sinkScrollContainerRef.current.scrollTop = savedPos;
      } else {
        scrollToBottom(sinkMessagesEndRef, sinkScrollContainerRef, { force: true, behavior: modeChanged ? 'smooth' : 'auto' });
      }
      lastActiveRoomId.current = sinkRoomId;
      lastSinkViewMode.current = sinkViewMode;
      lastInputMode.current = inputMode;
    } else {
      const lastMessage = sinkMessages[sinkMessages.length - 1];
      const isUser = (lastMessage?.source ?? lastMessage?.role) === 'user';
      scrollToBottom(sinkMessagesEndRef, sinkScrollContainerRef, { force: isUser || isSaving });
    }
  }, [sinkMessages, sinkViewMode, isSaving, sinkRoomId, inputMode]);

  // Scroll to bottom when keyboard opens on mobile
  useEffect(() => {
    if (isKeyboardOpen && sinkViewMode === 'input') {
      scrollToBottom(sinkMessagesEndRef, sinkScrollContainerRef, { force: true });
    }
  }, [isKeyboardOpen, sinkViewMode]);

  // Auto-expand Sync Tray when knowledge is referenced in Sink
  useEffect(() => {
    if (!isLargeScreen || !sinkRoomId || sinkMessages.length === 0) return;
    if (lastCheckedSinkRoomId.current === sinkRoomId) return;

    const hasReferences = sinkMessages.some(m =>
      m.metadata?.referencedAssetIds?.length > 0 ||
      m.metadata?.referencedNoteIds?.length > 0 ||
      m.metadata?.referencedInsightIds?.length > 0
    );

    if (hasReferences) {
      // Use setTimeout to avoid cascading renders warning by moving state updates out of the synchronous effect execution
      setTimeout(() => {
        setIsSyncTrayExpanded(true);
        // Decide initial view mode based on references
        const anyAsset = sinkMessages.some(m => m.metadata?.referencedAssetIds?.length > 0);
        const anyInsight = sinkMessages.some(m => m.metadata?.referencedInsightIds?.length > 0);
        if (anyAsset) setAssetViewMode('assets');
        else if (anyInsight) setAssetViewMode('insights');
        else setAssetViewMode('notes');
      }, 0);
    }

    lastCheckedSinkRoomId.current = sinkRoomId;
  }, [sinkRoomId, sinkMessages, isLargeScreen]);



  const handleSaveSettings = (e) => {
    e.preventDefault();
    localStorage.setItem(SETTINGS_KEYS.provider, llmProvider);
    localStorage.setItem(SETTINGS_KEYS.model, llmModel);
    localStorage.setItem(SETTINGS_KEYS.modelLight, llmModelLight);
    localStorage.setItem(SETTINGS_KEYS.modelHeavy, llmModelHeavy);
    localStorage.setItem(SETTINGS_KEYS.openaiKey, openaiApiKey.trim());
    localStorage.setItem(SETTINGS_KEYS.anthropicKey, anthropicApiKey.trim());
    localStorage.setItem(SETTINGS_KEYS.geminiKey, geminiApiKey.trim());
    localStorage.setItem(SETTINGS_KEYS.debug, debugMode ? 'true' : 'false');
    if (isLargeScreen) {
      setIsSettingsModalOpen(false);
    } else {
      setActiveTab('input');
    }
  };

  function openSinkInput() {
    closeAllModals();
    setActiveTab('input');
    setSinkViewMode('input');
    setInputMode('input');
  }

  function openSinkNotes() {
    closeAllModals();
    setActiveTab('input');
    setSinkViewMode('notes');
  }

  function openSyncWorkspace(mode = 'insights') {
    closeAllModals();
    setActiveTab('sync');
    setAssetViewMode(mode);
    setIsSyncTrayExpanded(true);
  }

  const handleReferenceClick = (type, id) => {
    if (type === 'asset') {
      setSelectedAssetId(id);
      openSyncWorkspace('assets');
    } else if (type === 'note') {
      setSelectedNoteId(id);
      openSyncWorkspace('notes');
    } else if (type === 'insight') {
      setSelectedInsightId(id);
      openSyncWorkspace('insights');
    }
  };

  function openThinkWorkspace() {
    closeAllModals();
    setActiveTab('think');
    setSyncViewMode('map');
  }

  const handleUpdateRoomTitle = async (roomId, newTitle) => {
    try {
      if (IS_MOCK_DEMO) {
        setChatRooms(current => current.map(r => r.id === roomId ? { ...r, title: newTitle } : r));
      } else {
        await updateDoc(doc(db, 'chatRooms', roomId), {
          title: newTitle,
          updatedAt: serverTimestamp()
        });
      }
    } catch (error) {
      console.error('Failed to update room title:', error);
      Sentry.captureException(error, { tags: { action: 'handleUpdateRoomTitle' }, extra: { roomId } });
      alert('ルーム名の更新に失敗しました。');
    }
  };

  const handleDeleteRoom = async (roomIdToDelete) => {
    if (!window.confirm('このチャット履歴を削除しますか？\n（関連するメッセージも削除されますが、NoteやHintは残ります）')) {
      return;
    }

    try {
      if (IS_MOCK_DEMO) {
        setChatRooms(current => current.filter(r => r.id !== roomIdToDelete));
        // Note: Mock messages are derived or static in this demo, real deletion handles them
      } else {
        // Delete room document
        await deleteDoc(doc(db, 'chatRooms', roomIdToDelete));

        // Delete associated messages
        const q = query(collection(db, 'chatMessages'), where('roomId', '==', roomIdToDelete), where('ownerUid', '==', user?.uid));
        const snapshot = await getDocs(q);
        const deletePromises = snapshot.docs.map(messageDoc => deleteDoc(messageDoc.ref));
        await Promise.all(deletePromises);
      }

      // Clear states if active rooms were deleted
      if (sinkRoomId === roomIdToDelete) {
        setSinkRoomId('');
        setSinkInputText('');
        localStorage.removeItem(`secondBrain.${user?.uid}.sinkRoomId`);
      }
    } catch (error) {
      console.error('Failed to delete chat room:', error);
      if (isOfflineError(error)) {
        alert('オフラインのため、チャットの削除に失敗しました。インターネット接続を確認してから再度お試しください。');
      } else {
        Sentry.captureException(error, { tags: { action: 'handleDeleteRoom' }, extra: { roomId: roomIdToDelete } });
        alert('チャットの削除に失敗しました。');
      }
    }
  };

  const handleStartNewRoom = (contextRefs = [], initialTitle = null, mode = null) => {
    setNewChatContextRefs(contextRefs || []);
    setNewChatInitialTitle(initialTitle);
    setNewChatInitialMode(mode);
    setIsNewChatConfirmOpen(true);
  };

  const handleDiscussInSink = (type, id, title) => {
    const contextRefs = [{ type, id }];
    const displayTitle = type === 'tag'
      ? `タグ「${title}」について議論`
      : `「${title}」について議論`;
    handleStartNewRoom(contextRefs, displayTitle, 'discuss');
  };

  const handleDiscussThinkNode = (node) => {
    if (!node) return;

    let type = node.type;
    let id = node.entityId || node.id;

    if (type === 'raw') type = 'note';
    if (type === 'asset') type = 'concept';
    if (type === 'tag') {
      id = node.tagName || node.label.replace(/^#/, '');
    }

    handleDiscussInSink(type, id, node.label);
  };

  const executeNewRoom = (contextRefs = [], initialTitle = null, mode = null) => {
    const storageKey = `secondBrain.${user?.uid}.sinkRoomId`;

    const sharedAssetRefs = (contextRefs || []).filter(ref => ref.type === 'sharedAsset');
    if (sharedAssetRefs.length > 0 && !import.meta.env.SSR) {
      const isAiMode = (mode || inputMode) === 'discuss' || (mode || inputMode) === 'anti';
      const unauthorizedRef = sharedAssetRefs.find(ref => (
        ref.sourceApp === 'prob'
          ? !canDiscussSharedAssetRef(userData, ref)
          : (isAiMode ? !canDiscussSharedAssetRef(userData, ref) : !canViewSharedAssets(userData))
      ));
      if (unauthorizedRef) {
        const message = unauthorizedRef.sourceApp === 'prob'
          ? 'Probレポートの閲覧権限がありません。'
          : 'Shared Assetとの議論権限がありません。プランをアップグレードしてください。';
        showToast(message, 'warning');
        return;
      }
    }

    if (IS_MOCK_DEMO) {
      const roomId = createMockId('mock-room-sink');
      setChatRooms((current) => [{
        id: roomId,
        title: initialTitle || '新しいデモメモ',
        mode: mode || inputMode,
        status: CHAT_ROOM_STATUS.ACTIVE,
        summaryNoteId: null,
        contextRefs: contextRefs || [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        lastMessageAt: new Date().toISOString(),
      }, ...current]);
      setSinkRoomId(roomId);
      setSinkInputText('');
      setHistoryIndex(null);
      setNewChatContextRefs([]);
      setNewChatInitialTitle(null);
      setNewChatInitialMode(null);
      setIsNewChatConfirmOpen(false);
      setActiveTab('input');
      setSinkViewMode('input');
      if (mode) setInputMode(mode);
      return;
    }

    localStorage.removeItem(storageKey);
    setSinkRoomId('');
    setSinkInputText('');
    setPendingSinkContextRefs(contextRefs || []);
    setPendingSinkRoomTitle(initialTitle);
    setHistoryIndex(null);
    setNewChatContextRefs([]);
    setNewChatInitialTitle(null);
    setNewChatInitialMode(null);
    setIsNewChatConfirmOpen(false);
    setActiveTab('input');
    setSinkViewMode('input');
    if (mode) setInputMode(mode);
  };

  const handleConfirmNewChat = () => {
    executeNewRoom(newChatContextRefs, newChatInitialTitle, newChatInitialMode);
  };

  const handleSummarizeAndNewRoom = async () => {
    try {
      // We pass saveMode: 'new' to ensure we create a new note
      await handleRunMemoization(null, { saveMode: 'new', skipNavigation: true });
      executeNewRoom();
      openSinkInput();
    } catch (error) {
      console.error('Failed to summarize and start new room:', error);
    }
  };

  const openMemoModal = () => {
    const activeRoomId = sinkRoomId;
    const room = chatRooms.find(r => r.id === activeRoomId);

    if (!activeRoomId || isSummarizing) return;
    
    const existingSummaryNoteId = room?.summaryNoteId;
    setMemoSaveMode(existingSummaryNoteId ? 'overwrite' : 'new');
    setIsMemoModalOpen(true);
  };

  const getCurrentApiKey = () => {
    const apiKeyByProvider = {
      openai: openaiApiKey,
      anthropic: anthropicApiKey,
      gemini: geminiApiKey,
    };
    return apiKeyByProvider[llmProvider]?.trim();
  };

  const getModelForAction = (action_type) => {
    const currentApiKey = getCurrentApiKey();
    if (!currentApiKey) return llmModel || getProviderModel(llmProvider);
    if (LIGHT_ACTIONS.has(action_type) && llmModelLight) return llmModelLight;
    if (HEAVY_ACTIONS.has(action_type) && llmModelHeavy) return llmModelHeavy;
    return llmModel || getProviderModel(llmProvider);
  };

  const loadSharedAssetPromptContexts = async (contextRefs = []) => {
    if (IS_MOCK_DEMO) return { text: '', referencedSharedAssetIds: [] };

    const sharedRefs = (contextRefs || [])
      .filter(ref => ref?.type === 'sharedAsset' && isValidSharedAssetId(ref.id));
    const uniqueRefs = Array.from(new Map(sharedRefs.map(ref => [ref.id, ref])).values());
    if (uniqueRefs.length === 0) return { text: '', referencedSharedAssetIds: [] };

    const contextBlocks = [];
    const referencedSharedAssetIds = [];

    for (const ref of uniqueRefs) {
      const snapshot = await getDoc(doc(db, 'sharedAssets', ref.id));
      if (!snapshot.exists()) {
        throw new Error('Probレポートが見つかりません。');
      }

      const sharedAsset = { id: snapshot.id, ...snapshot.data() };
      const permissionRef = { ...ref, sourceApp: sharedAsset.sourceApp || ref.sourceApp };
      if (!canDiscussSharedAssetRef(userData, permissionRef)) {
        throw new Error(permissionRef.sourceApp === 'prob'
          ? 'Probレポートの閲覧権限がありません。'
          : 'Shared Assetとの議論権限がありません。');
      }

      if (sharedAsset.sourceApp === 'prob') {
        if (!isProbReportSharedAsset(sharedAsset)) {
          throw new Error('Probレポートが公開済みShared Assetとして利用できません。');
        }
        contextBlocks.push(formatProbReportForBuddy(sharedAsset));
        referencedSharedAssetIds.push(sharedAsset.id);
      } else {
        const fallbackText = sharedAsset.body || sharedAsset.summary || '';
        if (fallbackText) {
          contextBlocks.push(`# Shared Asset\nid: ${sharedAsset.id}\ntitle: ${sharedAsset.title || ''}\n\n${fallbackText}`);
          referencedSharedAssetIds.push(sharedAsset.id);
        }
      }
    }

    return {
      text: contextBlocks.join('\n\n---\n\n'),
      referencedSharedAssetIds,
    };
  };

  const ensureChatRoom = async (firstMessageText, overrideMode = null) => {
    const modeToUse = overrideMode || inputMode;
    const activeRoomId = sinkRoomId;
    const storageKey = `secondBrain.${user?.uid}.sinkRoomId`;

    if (activeRoomId) return activeRoomId;

    if (IS_MOCK_DEMO) {
      const roomId = createMockId('mock-room-sink');
      setChatRooms((current) => [{
        id: roomId,
        title: (pendingSinkRoomTitle || firstMessageText || 'Untitled').slice(0, MAX_TITLE_LENGTH),
        mode: modeToUse,
        status: CHAT_ROOM_STATUS.ACTIVE,
        summaryNoteId: null,
        contextRefs: pendingSinkContextRefs,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        lastMessageAt: new Date().toISOString(),
      }, ...current]);
      setPendingSinkContextRefs([]);
      setPendingSinkRoomTitle(null);
      setSinkRoomId(roomId);
      return roomId;
    }

    try {
      const roomRef = await addDoc(collection(db, 'chatRooms'), {
        ownerUid: user?.uid,
        title: (pendingSinkRoomTitle || firstMessageText || 'Untitled').slice(0, MAX_TITLE_LENGTH),
        mode: modeToUse,
        status: CHAT_ROOM_STATUS.ACTIVE,
        summaryNoteId: null,
        contextRefs: pendingSinkContextRefs,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastMessageAt: serverTimestamp(),
      });

      setPendingSinkContextRefs([]);
      setPendingSinkRoomTitle(null);

      localStorage.setItem(storageKey, roomRef.id);
      setSinkRoomId(roomRef.id);
      return roomRef.id;
    } catch (error) {
      console.error('Failed to ensure chat room:', error);
      Sentry.captureException(error, { tags: { action: 'ensureChatRoom' } });
      throw error;
    }
  };

  const handleInputKeyDown = (e) => {
    // 1. Send logic (depends on isMultilineMode)
    const isSendTrigger = isMultilineMode 
      ? (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) // Editor Mode: Cmd/Ctrl+Enter
      : (e.key === 'Enter' && !e.shiftKey);           // Chat Mode: Enter (without Shift)

    if (isSendTrigger) {
      e.preventDefault();
      handleSave(e, inputMode);
      return;
    }

    // 2. History logic (only if not sending and it's Up/Down)
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
    if (e.nativeEvent?.isComposing || e.isComposing) return;
    if (userInputHistory.length === 0) return;

    // In textarea/editor mode, preserve native ArrowUp/ArrowDown caret navigation
    // while a draft exists; only allow history recall once the draft is empty.
    const currentText = sinkInputText;
    const target = e.target;
    const isMultilineTarget = isMultilineMode || target?.tagName === 'TEXTAREA';
    if (isMultilineTarget && currentText.length > 0) {
      return;
    }

    // Only trigger history when cursor is at the end with no active selection.
    if (
      target
      && typeof target.selectionStart === 'number'
      && typeof target.selectionEnd === 'number'
    ) {
      const hasSelection = target.selectionStart !== target.selectionEnd;
      const isCursorAtEnd = target.selectionEnd === currentText.length;
      if (hasSelection || !isCursorAtEnd) {
        return;
      }
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const nextIndex = historyIndex === null
        ? userInputHistory.length - 1
        : Math.max(historyIndex - 1, 0);

      setHistoryIndex(nextIndex);
      setSinkInputText(userInputHistory[nextIndex]);
      return;
    }

    if (historyIndex === null) return;

    e.preventDefault();
    const nextIndex = historyIndex + 1;
    if (nextIndex >= userInputHistory.length) {
      setHistoryIndex(null);
      setSinkInputText('');
      return;
    }

    setHistoryIndex(nextIndex);
    setSinkInputText(userInputHistory[nextIndex]);
  };

  const handleSave = async (e, overrideMode = null, overrideText = null) => {
    if (e) e.preventDefault();
    const modeToUse = overrideMode || inputMode;
    const text = (overrideText ?? sinkInputText).trim();
    const isAiMode = modeToUse === 'discuss' || modeToUse === 'anti';

    if (!text || isSaving) return;

    if (text.length > SINK_INPUT_CHAR_LIMIT) {
      showToast(`入力は${SINK_INPUT_CHAR_LIMIT.toLocaleString()}字以内にしてください。`, 'warning');
      return;
    }

    if (isAiMode && text.length > AI_ACTIVE_INPUT_CHAR_LIMIT) {
      showToast(`AIモードでは${AI_ACTIVE_INPUT_CHAR_LIMIT.toLocaleString()}字以内にしてください。長文はSinkで保存してからNoteまとめしてください。`, 'warning');
      return;
    }

    if (isSinkRoomAtLimit) {
      alert('このSinkは上限に達しました。Noteまとめしてから新しいSinkを開始してください。');
      return;
    }

    // Shared Asset discussion permission check
    const effectiveContextRefs = currentRoom?.contextRefs?.length > 0 ? currentRoom.contextRefs : pendingSinkContextRefs;
    const sharedAssetRefs = (effectiveContextRefs || []).filter(ref => ref.type === 'sharedAsset');
    const unauthorizedSharedAssetRef = sharedAssetRefs.find(ref => (
      ref.sourceApp === 'prob'
        ? !canDiscussSharedAssetRef(userData, ref)
        : (isAiMode && !canDiscussSharedAssetRef(userData, ref))
    ));
    if (unauthorizedSharedAssetRef) {
      const message = unauthorizedSharedAssetRef.sourceApp === 'prob'
        ? 'Probレポートの閲覧権限がありません。'
        : 'Shared Assetとの議論権限がありません。プランをアップグレードしてください。';
      showToast(message, 'warning');
      return;
    }

    const userMessageMetadata = {
      contextIds: selectedSyncNode ? [selectedSyncNode.id] : selectedAsset ? [selectedAsset.id] : selectedInsight ? [selectedInsight.id] : selectedNote ? [selectedNote.id] : [],
    };
    if (sinkDraftMetadata?.inputKind === 'paste') {
      Object.assign(userMessageMetadata, {
        inputKind: 'paste',
        captureType: 'external_clip',
        sourceType: sinkDraftMetadata.sourceType || 'unknown',
        charCount: text.length,
        pastedCharCount: sinkDraftMetadata.pastedCharCount || text.length,
        longPaste: text.length >= LONG_PASTE_CHAR_THRESHOLD,
        detectedAt: sinkDraftMetadata.detectedAt,
      });
    }

    let activeRoomId = sinkRoomId;

    try {
      setIsSaving(true);
      setHistoryIndex(null);
      setSinkInputText('');
      setSinkDraftMetadata(null);

      const isProbLinked = currentRoom?.contextRefs?.some(ref => ref.type === 'sharedAsset' && ref.sourceApp === 'prob') ||
                          pendingSinkContextRefs?.some(ref => ref.type === 'sharedAsset' && ref.sourceApp === 'prob');

      const roomId = await ensureChatRoom(text, modeToUse);
      activeRoomId = roomId;

      if (modeToUse !== 'input' && !checkAiAvailability()) {
        const errorData = {
          id: createMockId('local-error'),
          ownerUid: user?.uid,
          roomId,
          body: '[API_KEY_MISSING] APIキーが設定されていないため、AI機能を利用できません。',
          mode: modeToUse,
          source: 'assistant',
          kind: CHAT_MESSAGE_KIND.ERROR,
          createdAt: new Date().toISOString(),
          metadata: {
            error: 'API key missing',
            provider: llmProvider,
            model: llmModel || getProviderModel(llmProvider),
            finishReason: ERROR_FINISH_REASON,
            retryData: { text, mode: modeToUse },
          },
        };
        setLocalMessages(prev => [...prev, errorData]);
        setIsSaving(false);
        return;
      }

      if (IS_MOCK_DEMO) {
        const now = new Date().toISOString();
        const mockUserMessageMetadata = { ...userMessageMetadata };
        if (pendingSharedAssetRef) {
          mockUserMessageMetadata.sharedAssetRef = { ...pendingSharedAssetRef };
          setPendingSharedAssetRef(null);
        }

        const userMessage = {
          id: createMockId('mock-message-sink'),
          roomId,
          text,
          mode: modeToUse,
          source: 'user',
          kind: CHAT_MESSAGE_KIND.RAW_NOTE,
          createdAt: now,
          metadata: mockUserMessageMetadata,
        };
        const nextMessages = [...messages, userMessage];

        if (modeToUse === 'input') {
          const userMessageCount = nextMessages.filter(m => m.roomId === roomId && isUserMessage(m)).length;
          const suggestionInterval = isProbLinked ? PROB_INPUT_MEMO_SUGGESTION_INTERVAL : INPUT_MEMO_SUGGESTION_INTERVAL;
          const suggestion = userMessageCount % suggestionInterval === 0
            ? [{
              id: createMockId('mock-message'),
              roomId,
              text: buildMemoSuggestionText(messages, text, userMessageCount),
              mode: modeToUse,
              source: 'assistant',
              kind: CHAT_MESSAGE_KIND.MEMO_SUGGESTION,
              metadata: {
                generatedBy: 'mock',
                userMessageCount,
              },
              createdAt: now,
            }]
            : [];
          setMessages([...nextMessages, ...suggestion]);
        } else {
          let selectedAssetIds = [];
          if (concepts.length > 0) {
            const index = messages.length % concepts.length;
            selectedAssetIds = [concepts[index].id];
          }
          const assistantMessage = {
            id: createMockId('mock-message'),
            roomId,
            text: buildMockReply(modeToUse, text),
            mode: modeToUse,
            source: 'assistant',
            kind: CHAT_MESSAGE_KIND.CHAT_REPLY,
            metadata: {
              provider: 'mock',
              model: 'demo',
              finishReason: 'completed',
              referencedAssetIds: selectedAssetIds,
            },
            createdAt: now,
          };
          setMessages([...nextMessages, assistantMessage]);
        }

        setChatRooms((current) => current.map((room) => (
          room.id === roomId
            ? {
              ...room,
              title: room.title === '新しいデモメモ' || room.title === 'Untitled' ? text.slice(0, MAX_TITLE_LENGTH) : room.title,
              mode: modeToUse,
              updatedAt: now,
              lastMessageAt: now,
            }
            : room
        )));
        return;
      }

      const messageMetadata = {
        contextIds: selectedSyncNode ? [selectedSyncNode.id] : selectedAsset ? [selectedAsset.id] : selectedInsight ? [selectedInsight.id] : selectedNote ? [selectedNote.id] : [],
      };
      if (pendingSharedAssetRef) {
        // We only attach it to the first message after setting it from URL
        messageMetadata.sharedAssetRef = { ...pendingSharedAssetRef };
        setPendingSharedAssetRef(null);
      }

      await addDoc(collection(db, 'chatMessages'), {
        ownerUid: user?.uid,
        roomId,
        body: text,
        mode: modeToUse,
        source: 'user',
        kind: CHAT_MESSAGE_KIND.RAW_NOTE,
        createdAt: serverTimestamp(),
        metadata: messageMetadata,
      });

      if (modeToUse === 'input') {
        const userMessageCount = messages.filter(m => m.roomId === roomId && isUserMessage(m)).length + 1;
        const suggestionInterval = isProbLinked ? PROB_INPUT_MEMO_SUGGESTION_INTERVAL : INPUT_MEMO_SUGGESTION_INTERVAL;

        if (userMessageCount % suggestionInterval === 0) {
          await addDoc(collection(db, 'chatMessages'), {
            ownerUid: user?.uid,
            roomId,
            body: buildMemoSuggestionText(messages, text, userMessageCount),
            mode: modeToUse,
            source: 'assistant',
            kind: CHAT_MESSAGE_KIND.MEMO_SUGGESTION,
            metadata: {
              generatedBy: 'local',
              userMessageCount,
            },
            createdAt: serverTimestamp(),
          });
        }
        await updateDoc(doc(db, 'chatRooms', roomId), {
          mode: modeToUse,
          updatedAt: serverTimestamp(),
          lastMessageAt: serverTimestamp(),
        });
        return;
      }

      const apiKey = getCurrentApiKey();

      let selectedAssetIds = [];
      let selectedNoteIds = [];
      let selectedInsightIds = [];
      let selectedSharedAssetIds = [];
      let selectedKnowledgeText = '';

      const syncCurrentNode = syncHistory[syncHistory.length - 1];
      const fullSyncCurrentNode = syncCurrentNode ? syncGraph.nodesById.get(syncCurrentNode.id) : null;
      const thinkContext = syncCurrentNode.id === 'root'
        ? 'ルート (全体マップ)'
        : `[${getDisplayEntityType(fullSyncCurrentNode?.type || 'note')}] ${syncCurrentNode.label}`;

      if (modeToUse !== 'input') {
        // Keyword heuristic logic
        const queryText = text.toLowerCase();
        const currentContextLabel = (fullSyncCurrentNode?.label || syncCurrentNode?.label || '').toLowerCase().replace(/^#/, '');
        
        const scoreItem = (item) => {
          let score = 0;
          const title = (item.title || '').toLowerCase();
          const tags = (item.tags || []).map(t => t.toLowerCase());
          
          // Boost if item's tag is directly mentioned in the user's input
          tags.forEach(tag => {
            if (queryText.includes(tag)) score += 3;
          });
          
          // Boost if item's title is partially matched in the input or vice versa
          if (title && title.length > 2 && queryText.includes(title)) score += 2;
          
          // Boost if it relates to the current think context
          if (currentContextLabel && (title.includes(currentContextLabel) || tags.includes(currentContextLabel))) score += 2;
          if (syncCurrentNode && item.id === syncCurrentNode.id) score += 5;

          // Simple keyword matching as fallback
          const queryTerms = queryText.split(/[\s,。、]+/).filter(t => t.length > 1);
          const content = `${title} ${item.body || item.summary || ''} ${tags.join(' ')}`;
          score += queryTerms.reduce((s, term) => s + (content.includes(term) ? 1 : 0), 0);
          
          return score;
        };
        const byScore = (a, b) => scoreItem(b) - scoreItem(a);
        const isRelevant = (item) => scoreItem(item) > 0;

        const currentIgnoredIds = ignoredAssetIdsByRoom[sinkRoomId] || [];
        const activeConcepts = concepts.filter(c =>
          c.status !== ASSET_STATUS.ARCHIVED &&
          c.status !== ASSET_STATUS.DEPRECATED &&
          !currentIgnoredIds.includes(c.id)
        );
        const activeNotes = notes.filter(n => n.status !== NOTE_STATUS.ARCHIVED);
        const activeInsights = insights.filter(i =>
          i.status === INSIGHT_STATUS.SUGGESTED &&
          !i.type?.startsWith('duplicate') &&
          i.type !== INSIGHT_TYPE.NOTE_UPDATE &&
          i.type !== INSIGHT_TYPE.ASSET_UPDATE &&
          i.type !== INSIGHT_TYPE.TAG_MERGE
        );

        const candidateConcepts = activeConcepts.filter(isRelevant).sort(byScore).slice(0, 10);
        const candidateNotes = activeNotes.filter(isRelevant).sort(byScore).slice(0, 5);
        const candidateInsights = activeInsights.filter(isRelevant).sort(byScore).slice(0, 5);

        // Fallback: prefer a wider array of recent assets if no matches
        const fallbackConcepts = candidateConcepts.length > 0 ? candidateConcepts : activeConcepts.slice(0, 10);
        const fallbackNotes = candidateNotes.length > 0 ? candidateNotes : activeNotes.slice(0, 5);
        const fallbackInsights = candidateInsights.length > 0 ? candidateInsights : activeInsights.slice(0, 5);

        const slimConcepts = JSON.stringify(fallbackConcepts.map(c => ({ id: c.id, title: c.title, tags: c.tags })));
        const slimNotes = JSON.stringify(fallbackNotes.map(n => ({ id: n.id, title: n.title, tags: n.tags })));
        const slimInsights = JSON.stringify(fallbackInsights.map(i => ({ id: i.id, title: i.title, tags: i.tags })));

        try {
          const selectionResult = await generateText({
            provider: llmProvider,
            apiKey,
            model: getModelForAction(ACTION_TYPE.CONTEXT_SELECT),
            userRole: userData?.role,
            userData: userData,
            action_type: ACTION_TYPE.CONTEXT_SELECT,
            maxOutputTokens: CONTEXT_SELECT_OUTPUT_TOKENS,
            metadata: { roomId, relatedAssetIds: [], relatedNoteIds: [], relatedInsightIds: [] },
            instructions: 'あなたは優秀なアシスタントです。指定されたフォーマットで関連するAsset, Note, HintのIDを抽出してください。',
            prompt: buildAssetSelectionPrompt({
              mode: modeToUse,
              history: messages,
              text,
              thinkContext,
              concepts: slimConcepts,
              notes: slimNotes,
              insights: slimInsights,
            }),
          });
          const parsed = extractJsonObject(selectionResult.text);
          if (parsed) {
            if (Array.isArray(parsed.referencedAssetIds)) selectedAssetIds = parsed.referencedAssetIds;
            if (Array.isArray(parsed.referencedNoteIds)) selectedNoteIds = parsed.referencedNoteIds;
            if (Array.isArray(parsed.referencedInsightIds)) selectedInsightIds = parsed.referencedInsightIds;
          }
        } catch (e) {
          console.error('Failed to select knowledge:', e);
        }

        const selectedConcepts = concepts.filter(c => selectedAssetIds.includes(c.id));
        const selectedNotes = notes.filter(n => selectedNoteIds.includes(n.id));
        const selectedInsights = insights.filter(i => selectedInsightIds.includes(i.id));

        selectedKnowledgeText = [
          ...selectedConcepts.map(c => `[Asset ID: ${c.id}, Title: ${c.title}]\n${c.body || c.summary || ''}`),
          ...selectedNotes.map(n => `[Note ID: ${n.id}, Title: ${n.title}]\n${n.body || n.summary || ''}`),
          ...selectedInsights.map(i => `[Hint ID: ${i.id}, Title: ${i.title}]\n${i.summary || ''}`),
        ].join('\n\n');
      }

      if (modeToUse !== 'input' && sharedAssetRefs.length > 0) {
        const sharedAssetContext = await loadSharedAssetPromptContexts(effectiveContextRefs);
        selectedSharedAssetIds = sharedAssetContext.referencedSharedAssetIds;
        selectedKnowledgeText = [
          selectedKnowledgeText,
          sharedAssetContext.text ? `参照Shared Asset:\n${sharedAssetContext.text}` : '',
        ].filter(Boolean).join('\n\n');
      }

      const aiResult = await generateText({
        provider: llmProvider,
        apiKey,
        model: getModelForAction(ACTION_TYPE.CHAT_REPLY),
        userRole: userData?.role,
        userData: userData,
        action_type: ACTION_TYPE.CHAT_REPLY,
        maxOutputTokens: isDeepDiscussMode ? DEEP_DISCUSS_OUTPUT_TOKENS : CHAT_OUTPUT_TOKENS,
        metadata: { 
          roomId, 
          relatedAssetIds: selectedAssetIds,
          relatedNoteIds: selectedNoteIds,
          relatedInsightIds: selectedInsightIds,
          relatedSharedAssetIds: selectedSharedAssetIds,
          hasSharedAssetContext: sharedAssetRefs.length > 0
        },
        instructions: [
          'あなたはSecond BrainアプリのAIアシスタントです。ユーザーの思考を保存し、深め、後からHint抽出やAsset化ができるように支援してください。',
          selectedSharedAssetIds.length > 0 ? PROB_REPORT_BUDDY_INSTRUCTIONS : '',
        ].filter(Boolean).join('\n\n'),
        prompt: buildThinkingPrompt({
          mode: modeToUse,
          history: messages,
          text,
          selectedAssetsText: selectedKnowledgeText,
          thinkContext,
          isDeepDiscuss: isDeepDiscussMode,
        }),
      });

      await addDoc(collection(db, 'chatMessages'), {
        ownerUid: user?.uid,
        roomId,
        body: aiResult.text,
        mode: modeToUse,
        source: 'assistant',
        kind: CHAT_MESSAGE_KIND.CHAT_REPLY,
        metadata: {
          provider: llmProvider,
          model: llmModel || getProviderModel(llmProvider),
          finishReason: aiResult.finishReason,
          referencedAssetIds: selectedAssetIds,
          referencedNoteIds: selectedNoteIds,
          referencedInsightIds: selectedInsightIds,
          referencedSharedAssetIds: selectedSharedAssetIds,
        },
        createdAt: serverTimestamp(),
      });

      // Asset reference tracking (Product Task 02)
      if (selectedAssetIds && selectedAssetIds.length > 0) {
        // Deduplicate per reply so one Asset is counted at most once for this assistant turn.
        const uniqueAssetIds = Array.from(new Set(selectedAssetIds));
        const conceptById = new Map(concepts.map((concept) => [concept.id, concept]));
        await Promise.all(uniqueAssetIds.map(async (assetId) => {
          const referencedAsset = conceptById.get(assetId);
          const now = new Date();
          const usage = deriveAssetUsageMetrics(referencedAsset, now);
          const nextReferenceCount = usage.referenceCount + 1;
          // referencesLast30Days is stored as rolling aggregate metadata (not a full event log).
          // If the previous reference is outside the 30-day window, restart the rolling counter at 1.
          const nextReferencesLast30Days =
            typeof usage.daysSinceLastReference === 'number' && usage.daysSinceLastReference > 30
              ? 1
              : usage.referencesLast30Days + 1;
          const nextUsageStatus = deriveAssetUsageStatus({
            referenceCount: nextReferenceCount,
            referencesLast30Days: nextReferencesLast30Days,
            daysSinceLastReference: 0,
            createdDaysAgo: usage.createdDaysAgo,
          });

          try {
            await updateDoc(doc(db, 'concepts', assetId), {
              referenceCount: increment(1),
              lastReferencedAt: serverTimestamp(),
              usageStatus: nextUsageStatus,
              'metadata.referenceCount': increment(1),
              'metadata.referencesLast30Days': nextReferencesLast30Days,
              'metadata.lastReferencedAt': serverTimestamp(),
              'metadata.usageCalculatedAt': serverTimestamp(),
              'metadata.usageRuleVersion': ASSET_USAGE_RULE_VERSION,
            });
          } catch (error) {
            // Non-blocking telemetry update: conversation should continue even if reference metrics update fails.
            console.warn('Failed to update asset reference count:', error);
            Sentry.captureException(error, { tags: { action: 'updateAssetReferenceCount' } });
          }
        }));
      }

      await updateDoc(doc(db, 'chatRooms', roomId), {
        mode: modeToUse,
        updatedAt: serverTimestamp(),
        lastMessageAt: serverTimestamp(),
      });

    } catch (error) {
      console.error('Failed to save message or generate AI reply:', error);
      Sentry.captureException(error, {
        tags: { action: 'handleSendMessage', mode: modeToUse },
        extra: { roomId: activeRoomId }
      });

      if (error.message.includes('402') || error.code === 'insufficient_credits' || error.code === 'heavy_action_blocked') {
        setCreditLimitMessage(error.message);
        setIsCreditLimitModalOpen(true);
        return;
      }
      
      const roomId = activeRoomId;
      if (roomId) {
        const errorData = {
          id: createMockId('local-error'),
          ownerUid: user?.uid,
          roomId,
          body: mapErrorToUserMessage(error),
          mode: modeToUse,
          source: 'assistant',
          kind: CHAT_MESSAGE_KIND.ERROR,
          createdAt: new Date().toISOString(),
          metadata: {
            error: error.message || 'Unknown error',
            provider: llmProvider,
            model: llmModel || getProviderModel(llmProvider),
            finishReason: error.finishReason || ERROR_FINISH_REASON,
            retryData: { text, mode: modeToUse },
          },
        };

        setLocalMessages(prev => [...prev, errorData]);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleRunMemoization = async (e, options = {}) => {
    if (e) e.preventDefault();
    if (isSummarizing) return;

    const {
      saveMode = memoSaveMode,
      hint = memoHint,
      roomToUse = null,
      noteToOverwrite = null,
      skipNavigation = false,
    } = options;
    // Note: target (sink/buddy) is no longer used for routing as of unified Sink transition.

    // Pattern C check
    if (CREDIT_UX_PATTERN === 'C' && userData?.role === 'credit_beta' && userData.usedCredits >= userData.grantedCredits * CREDIT_WARNING_THRESHOLD) {
      setCreditLimitMessage('クレジットが80%に達したため、Noteまとめは制限されています。チャットは継続可能です。');
      setIsCreditLimitModalOpen(true);
      return;
    }

    const activeRoomId = roomToUse?.id || sinkRoomId;
    const activeRoom = roomToUse || chatRooms.find((r) => r.id === activeRoomId) || null;

    let messagesToSummarize;
    if (roomToUse && !roomToUse.id) {
       // Should not happen if passed correctly
       messagesToSummarize = [];
    } else if (roomToUse && roomToUse.id) {
       // Fetch messages if they are not in the current room
       if (IS_MOCK_DEMO) {
         messagesToSummarize = mockMessages.filter(m => m.roomId === roomToUse.id);
       } else {
         const q = query(
           collection(db, 'chatMessages'),
           where('ownerUid', '==', user?.uid),
           where('roomId', '==', roomToUse.id),
           orderBy('createdAt', 'asc')
         );
         const snap = await getDocs(q);
         messagesToSummarize = snap.docs.map(d => ({ id: d.id, ...d.data() }));
       }
    } else {
       messagesToSummarize = messages.filter((m) => m.roomId === activeRoomId);
    }

    if (messagesToSummarize.length === 0) {
      alert('まとめ対象のメッセージがありません。');
      return;
    }

    const existingSummaryNoteId = noteToOverwrite?.id || activeRoom?.summaryNoteId;
    const shouldOverwriteExisting = saveMode === 'overwrite' && existingSummaryNoteId;

    if (!checkAiAvailability()) {
      openSettings();
      alert('APIキーが設定されていないため、Noteまとめを利用できません。Settingsから設定してください。');
      return;
    }

    if (IS_MOCK_DEMO) {
      try {
        setIsSummarizing(true);
        const summaryText = buildMockSummary(messagesToSummarize, hint.trim());
        const firstUserMessage = messagesToSummarize.find((message) => isUserMessage(message));
        const title = (hint.trim() || (firstUserMessage?.body ?? firstUserMessage?.text) || 'チャットサマリー').slice(0, MAX_TITLE_LENGTH);
        const summaryNoteId = shouldOverwriteExisting ? existingSummaryNoteId : createMockId('mock-note');
        
        const sourceMessageIds = messagesToSummarize.map((message) => message.id);
        if (sourceMessageIds.length === 0) {
          console.warn('Noteまとめ: sourceMessageIds is empty');
        }

        const existingNote = shouldOverwriteExisting ? notes.find(n => n.id === existingSummaryNoteId) : null;
        const revisionCount = (existingNote?.metadata?.revisionCount || 0) + (shouldOverwriteExisting ? 1 : 0);

        const noteData = {
          ownerUid: user?.uid,
          id: summaryNoteId,
          title,
          summary: summaryText.split('\n').find((line) => line.trim() && !line.startsWith('#')) || title,
          body: summaryText,
          type: 'chat_summary',
          status: NOTE_STATUS.INBOX,
          sourceRoomIds: [activeRoomId],
          sourceMessageIds,
          relatedConceptIds: shouldOverwriteExisting ? (existingNote?.relatedConceptIds || []) : [],
          relatedInsightIds: shouldOverwriteExisting ? (existingNote?.relatedInsightIds || []) : [],
          tags: sanitizeTags(hint.trim() ? [hint.trim()] : (shouldOverwriteExisting ? (existingNote?.tags || []) : ['デモメモ'])),
          createdAt: shouldOverwriteExisting ? (existingNote?.createdAt || new Date().toISOString()) : new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          metadata: {
            summaryCompleteness: 'complete',
            generatedBy: 'mock',
            memoHint: hint.trim(),
            revisionType: shouldOverwriteExisting ? 'ai_resummarize' : 'new',
            revisionCount,
            lastRevisedAt: shouldOverwriteExisting ? new Date().toISOString() : null,
            linkedRooms: activeRoom?.linkedRooms || [],
            sourceMode: 'sink',
          },
        };

        setNotes((current) => (
          shouldOverwriteExisting
            ? current.map((note) => (note.id === existingSummaryNoteId ? { ...note, ...noteData } : note))
            : [noteData, ...current]
        ));
        setChatRooms((current) => current.map((room) => (
          room.id === activeRoomId
            ? { ...room, status: CHAT_ROOM_STATUS.SUMMARIZED, summaryNoteId, updatedAt: new Date().toISOString() }
            : room
        )));
        setSelectedNoteId(summaryNoteId);
        if (!skipNavigation) {
          openSyncWorkspace('notes');
        } else {
          openSinkNotes();
        }
        setIsMemoModalOpen(false);
        return;
      } finally {
        setIsSummarizing(false);
      }
    }

    const apiKey = getCurrentApiKey();
    if (!apiKey && !user) {
      openSettings();
      alert('先に設定画面でAPIキーを保存するか、ログインしてください。');
      return;
    }

    try {
      setIsSummarizing(true);

      const sharedAssetRefs = (activeRoom?.contextRefs || []).filter(ref => ref.type === 'sharedAsset');
      const probQuotes = [];
      // 今回まとめる範囲で言及されたセクションキー（最初のリンクメッセージにのみ付与される）。
      const referencedSections = new Set(
        messagesToSummarize
          .map(m => m.metadata?.sharedAssetRef?.section)
          .filter(Boolean)
      );
      if (!IS_MOCK_DEMO && sharedAssetRefs.length > 0) {
        for (const ref of sharedAssetRefs) {
          try {
            const snapshot = await getDoc(doc(db, 'sharedAssets', ref.id));
            if (!snapshot.exists()) continue;

            const sharedAsset = { id: snapshot.id, ...snapshot.data() };
            const permissionRef = { ...ref, sourceApp: sharedAsset.sourceApp || ref.sourceApp };
            // 議論フローと同じ権限・公開状態チェックに統一する。失敗時は引用を付けずに継続する。
            if (!canDiscussSharedAssetRef(userData, permissionRef) || !isProbReportSharedAsset(sharedAsset)) {
              continue;
            }

            const sectionQuotes = [];
            referencedSections.forEach(secKey => {
              const sectionData = sharedAsset[secKey];
              if (sectionData?.summary) {
                sectionQuotes.push({
                  reportId: sharedAsset.id,
                  reportTitle: sharedAsset.title,
                  section: secKey,
                  summary: sectionData.summary,
                });
              }
            });

            if (sectionQuotes.length > 0) {
              probQuotes.push(...sectionQuotes);
            } else {
              // どのセクションの話か特定できない場合は、レポート全体の総括を引用候補にする。
              const overallSummary = sharedAsset.positioning?.bottomLine;
              if (overallSummary) {
                probQuotes.push({
                  reportId: sharedAsset.id,
                  reportTitle: sharedAsset.title,
                  section: 'レポート全体',
                  summary: overallSummary,
                });
              }
            }
          } catch (error) {
            console.warn('Failed to load shared asset for prob quote:', ref.id, error);
          }
        }
      }

      const summaryResult = await generateText({
        provider: llmProvider,
        apiKey,
        model: getModelForAction(ACTION_TYPE.MEMOIZE),
        userRole: userData?.role,
        userData: userData,
        action_type: ACTION_TYPE.MEMOIZE,
        metadata: { roomId: activeRoomId, relatedAssetIds: [], relatedNoteIds: [], relatedInsightIds: [] },
        instructions: 'あなたはチャットログを情報落ちなくNoteへまとめる編集者です。必ずJSON形式で出力してください。',
        prompt: buildSummaryPrompt(messagesToSummarize, { memoHint: hint.trim(), probQuotes }),
        maxOutputTokens: SUMMARY_OUTPUT_TOKENS,
        timeoutMs: SUMMARY_TIMEOUT_MS,
      });

      let parsed;
      try {
        parsed = extractJsonObject(summaryResult.text);
      } catch (e) {
        console.warn('Failed to parse summary JSON, falling back to text processing', e);
        const text = summaryResult.text;
        const firstUserMessage = messagesToSummarize.find((message) => isUserMessage(message));
        parsed = {
          title: (hint.trim() || (firstUserMessage?.body ?? firstUserMessage?.text) || 'チャットサマリー').slice(0, MAX_TITLE_LENGTH),
          summary: text.split('\n').find((line) => line.trim()) || 'チャットサマリー',
          body: text,
          tags: []
        };
      }

      const sourceMessageIds = messagesToSummarize.map((message) => message.id);
      if (sourceMessageIds.length === 0) {
        console.warn('Noteまとめ: sourceMessageIds is empty');
      }

      const existingNote = shouldOverwriteExisting ? notes.find(n => n.id === existingSummaryNoteId) : null;
      const revisionCount = (existingNote?.metadata?.revisionCount || 0) + (shouldOverwriteExisting ? 1 : 0);

      const noteData = {
        ownerUid: user?.uid,
        title: (parsed.title || '無題のノート').slice(0, MAX_TITLE_LENGTH),
        summary: parsed.summary || '',
        body: parsed.body || '',
        type: 'chat_summary',
        status: NOTE_STATUS.INBOX,
        sourceRoomIds: [activeRoomId],
        sourceMessageIds,
        relatedConceptIds: shouldOverwriteExisting ? (existingNote?.relatedConceptIds || []) : [],
        relatedInsightIds: shouldOverwriteExisting ? (existingNote?.relatedInsightIds || []) : [],
        tags: sanitizeTags(Array.from(new Set([
          ...(Array.isArray(parsed.tags) ? parsed.tags : []), 
          ...(hint.trim() ? [hint.trim()] : []),
          ...(shouldOverwriteExisting ? (existingNote?.tags || []) : [])
        ]))),
        createdAt: shouldOverwriteExisting ? (existingNote?.createdAt || serverTimestamp()) : serverTimestamp(),
        updatedAt: serverTimestamp(),
        metadata: {
          summaryCompleteness: isNormalFinishReason(summaryResult.finishReason) ? 'complete' : 'partial',
          generatedBy: 'ai',
          provider: llmProvider,
          model: llmModel || getProviderModel(llmProvider),
          finishReason: summaryResult.finishReason,
          memoHint: hint.trim(),
          revisionType: shouldOverwriteExisting ? 'ai_resummarize' : 'new',
          revisionCount,
          lastRevisedAt: shouldOverwriteExisting ? serverTimestamp() : null,
          linkedRooms: activeRoom?.linkedRooms || [],
          sourceMode: 'sink',
        },
      };
      let summaryNoteId = shouldOverwriteExisting ? existingSummaryNoteId : null;

      if (shouldOverwriteExisting) {
        await updateDoc(doc(db, 'notes', existingSummaryNoteId), { ...noteData, updatedAt: serverTimestamp() });
        const previousNoteTags = existingNote?.tags || [];
        if (!IS_MOCK_DEMO) await syncNormalizedTags(db, user?.uid, existingSummaryNoteId, 'note', noteData.tags, previousNoteTags);
      } else {
        const noteRef = await addDoc(collection(db, 'notes'), noteData);
        summaryNoteId = noteRef.id;
        if (!IS_MOCK_DEMO) await syncNormalizedTags(db, user?.uid, summaryNoteId, 'note', noteData.tags);
      }

      await updateDoc(doc(db, 'chatRooms', activeRoomId), { status: CHAT_ROOM_STATUS.SUMMARIZED, summaryNoteId, updatedAt: serverTimestamp() });
      setSelectedNoteId(summaryNoteId);
      if (!skipNavigation) {
        openSyncWorkspace('notes');
      } else {
        openSinkNotes();
      }
      setIsMemoModalOpen(false);
    } catch (error) {
      console.error('Failed to summarize chat:', error);
      if (isOfflineError(error)) {
        alert('オフラインのため、Noteまとめに失敗しました。インターネット接続を確認してから再度お試しください。');
      } else {
        Sentry.captureException(error, {
          tags: { action: 'handleRunMemoization' },
          extra: { roomId: activeRoomId }
        });
        if (error.message.includes('402') || error.code === 'insufficient_credits' || error.code === 'heavy_action_blocked') {
          setCreditLimitMessage(error.message);
          setIsCreditLimitModalOpen(true);
        } else {
          alert(`Noteまとめに失敗しました: ${error.message}`);
        }
      }
    } finally {
      setIsSummarizing(false);
    }
  };

  const onSubmitManualNote = async (noteData) => {
    try {
      const createdNote = await handleCreateNote(noteData);
      if (!IS_MOCK_DEMO && createdNote.tags?.length > 0) {
        await syncNormalizedTags(db, user?.uid, createdNote.id, 'note', createdNote.tags);
      }
      setIsNoteCreateModalOpen(false);
      setSelectedNoteId(createdNote.id);
    } catch (error) {
      console.error('Failed to create manual note:', error);
      Sentry.captureException(error, { tags: { action: 'onSubmitManualNote' } });
      alert(`メモの作成に失敗しました: ${error.message}`);
    }
  };

  const handleCrystallizeNote = async () => {
    if (!selectedNote || isCrystallizing) return;

    if (!checkAiAvailability()) {
      openSettings();
      alert('APIキーが設定されていないため、Hint抽出を利用できません。Settingsから設定してください。');
      return;
    }

    // Pattern C check
    if (CREDIT_UX_PATTERN === 'C' && userData?.role === 'credit_beta' && userData.usedCredits >= userData.grantedCredits * CREDIT_WARNING_THRESHOLD) {
      setCreditLimitMessage('クレジットが80%に達したため、Hint抽出は制限されています。チャットは継続可能です。');
      setIsCreditLimitModalOpen(true);
      return;
    }

    if (IS_MOCK_DEMO) {
      try {
        setIsCrystallizing(true);
        const candidates = buildMockInsightCandidates(selectedNote);
        const now = new Date().toISOString();
        const newInsights = candidates.map((candidate) => ({
          id: createMockId('mock-insight'),
          title: candidate.title,
          summary: candidate.summary,
          type: candidate.type,
          suggestedTargetType: normalizeAssetType(candidate.suggestedTargetType),
          status: INSIGHT_STATUS.SUGGESTED,
          confidence: candidate.confidence,
          sourceNoteIds: [selectedNote.id],
          sourceRoomIds: selectedNote.sourceRoomIds || [],
          sourceMessageIds: selectedNote.sourceMessageIds || [],
          relatedConceptIds: [],
          tags: sanitizeTags(candidate.tags),
          createdAt: now,
          updatedAt: now,
          resolvedAt: null,
          metadata: { provider: 'mock', model: 'demo', finishReason: 'completed' },
        }));

        setInsights((current) => [...newInsights, ...current]);
        setNotes((current) => current.map((note) => (
          note.id === selectedNote.id
            ? { ...note, relatedInsightIds: Array.from(new Set([...(note.relatedInsightIds || []), ...newInsights.map((insight) => insight.id)])), updatedAt: now }
            : note
        )));
        setSelectedInsightSourceNoteId(selectedNote.id);
        setSelectedInsightStatus(INSIGHT_STATUS.SUGGESTED);
        setGlobalSelectedTags([]);
        setSelectedInsightSourceIds([]);
        setSelectedInsightId(null);
        setSelectedAssetId(null);
        openSyncWorkspace('insights');
        return;
      } finally {
        setIsCrystallizing(false);
      }
    }

    const apiKey = getCurrentApiKey();
    if (!apiKey && !user) {
      openSettings();
      alert('先に設定画面でAPIキーを保存するか、ログインしてください。');
      return;
    }

    try {
      setIsCrystallizing(true);
      const result = await generateText({
        provider: llmProvider,
        apiKey,
        model: getModelForAction(ACTION_TYPE.EXTRACT_INSIGHT),
        userRole: userData?.role,
        userData: userData,
        action_type: ACTION_TYPE.EXTRACT_INSIGHT,
        metadata: { roomId: null, relatedAssetIds: [], relatedNoteIds: [selectedNote.id], relatedInsightIds: [] },
        instructions: 'あなたはSecond Brainの蒸留エージェントです。',
        prompt: buildInsightPrompt(selectedNote),
        maxOutputTokens: INSIGHT_OUTPUT_TOKENS,
        timeoutMs: INSIGHT_TIMEOUT_MS,
      });
      let parsed;
      try { parsed = extractJsonObject(result.text); } catch { parsed = buildFallbackInsight(result.text); }
      const candidates = Array.isArray(parsed.insights) ? parsed.insights.slice(0, 5) : [];

      if (candidates.length === 0) {
        showToast('このNoteは短期メモ寄りのため、Hint化せずNoteのまま残す候補でした。必要なら後でToDo / Projectとして整理できます。', 'info');
        return;
      }

      const newInsightsData = candidates.map(candidate => ({
        ownerUid: user?.uid,
        title: candidate.title || 'Untitled Hint',
        summary: candidate.summary || '',
        type: candidate.type || 'implicit_knowledge',
        suggestedTargetType: normalizeAssetType(candidate.suggestedTargetType),
        status: INSIGHT_STATUS.SUGGESTED,
        confidence: Number(candidate.confidence) || 0.5,
        sourceNoteIds: [selectedNote.id],
        sourceRoomIds: selectedNote.sourceRoomIds || [],
        sourceMessageIds: selectedNote.sourceMessageIds || [],
        relatedConceptIds: [],
        tags: sanitizeTags(Array.isArray(candidate.tags) && candidate.tags.length > 0 ? candidate.tags : selectedNote.tags || []),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        resolvedAt: null,
        metadata: {
          provider: llmProvider,
          model: llmModel || getProviderModel(llmProvider),
          finishReason: result.finishReason,
          parseFailed: !!candidate.metadata?.parseFailed,
          rawOutput: candidate.metadata?.rawOutput || null,
        },
      }));
      const insightRefs = await Promise.all(newInsightsData.map(data => addDoc(collection(db, 'insights'), data)));
      if (!IS_MOCK_DEMO) {
        await Promise.all(insightRefs.map((ref, index) => syncNormalizedTags(db, user?.uid, ref.id, 'insight', newInsightsData[index].tags)));
      }

      await updateDoc(doc(db, 'notes', selectedNote.id), {
        relatedInsightIds: arrayUnion(...insightRefs.map((insightRef) => insightRef.id)),
        updatedAt: serverTimestamp(),
      });
      setSelectedInsightSourceNoteId(selectedNote.id);
      setSelectedInsightStatus(INSIGHT_STATUS.SUGGESTED);
      setGlobalSelectedTags([]);
      setSelectedInsightSourceIds([]);
      setSelectedInsightId(null);
      setSelectedAssetId(null);
      openSyncWorkspace('insights');
    } catch (error) {
      console.error('Failed to crystallize note:', error);
      Sentry.captureException(error, {
        tags: { action: 'handleCrystallizeNote' },
        extra: { noteId: selectedNote?.id }
      });
      if (error.message.includes('402') || error.code === 'insufficient_credits' || error.code === 'heavy_action_blocked') {
        setCreditLimitMessage(error.message);
        setIsCreditLimitModalOpen(true);
      } else {
        alert(`Hint抽出に失敗しました: ${error.message}`);
      }
    } finally {
      setIsCrystallizing(false);
    }
  };

  const handleRunHealthCheck = async () => {
    if (isAuditing) return;

    if (!checkAiAvailability()) {
      openSettings();
      alert('APIキーが設定されていないため、ヘルスチェックを利用できません。Settingsから設定してください。');
      return;
    }

    if (IS_MOCK_DEMO) {
      try {
        setIsAuditing(true);
        const now = new Date().toISOString();
        const proposals = buildMockHealthProposals(notes, concepts);
        const mockInsights = proposals.map(p => ({
          id: createMockId('mock-insight'),
          ownerUid: user?.uid,
          kind: 'proposed_change',
          status: INSIGHT_STATUS.SUGGESTED,
          createdAt: now,
          updatedAt: now,
          metadata: { generatedBy: 'mock' },
          ...p
        }));
        setInsights(current => [...mockInsights, ...current]);
        alert('ヘルスチェック（デモ）を完了しました。更新候補を確認してください。');
      } finally {
        setIsAuditing(false);
      }
      return;
    }

    const apiKey = getCurrentApiKey();
    if (!apiKey && !user) {
      openSettings();
      alert('先に設定画面でAPIキーを保存するか、ログインしてください。');
      return;
    }

    try {
      setIsAuditing(true);
      const activeNotes = notes.filter(n => n.status !== NOTE_STATUS.ARCHIVED);
      const activeConcepts = concepts.filter(c => c.status !== ASSET_STATUS.ARCHIVED && c.status !== ASSET_STATUS.DEPRECATED);

      const healthCheckResult = await generateText({
        provider: llmProvider,
        apiKey,
        model: getModelForAction(ACTION_TYPE.AUDIT),
        userRole: userData?.role,
        userData: userData,
        action_type: ACTION_TYPE.AUDIT,
        maxOutputTokens: INSIGHT_OUTPUT_TOKENS,
        metadata: {
          relatedAssetIds: activeConcepts.map(c => c.id),
          relatedNoteIds: activeNotes.map(n => n.id)
        },
        instructions: 'あなたはSecond Brainのナレッジメンテナンス・エージェントです。',
        prompt: buildHealthCheckPrompt({
          notes: activeNotes,
          concepts: activeConcepts
        }),
      });

      const parsed = extractJsonObject(healthCheckResult.text);
      const proposals = Array.isArray(parsed?.proposals) ? parsed.proposals : [];

      if (proposals.length === 0) {
        alert('更新が必要な項目は見つかりませんでした。');
        return;
      }

      const newInsightsData = proposals.map(p => ({
        ownerUid: user?.uid,
        kind: 'proposed_change',
        title: p.title || '更新候補',
        summary: p.summary || '',
        type: p.type || INSIGHT_TYPE.NOTE_UPDATE,
        targetId: p.targetId,
        proposedPatch: {
          ...p.proposedPatch,
          ...(p.proposedPatch?.tags ? { tags: sanitizeTags(p.proposedPatch.tags) } : {}),
        },
        status: INSIGHT_STATUS.SUGGESTED,
        confidence: Number(p.confidence) || 0.5,
        sourceNoteIds: p.type === INSIGHT_TYPE.NOTE_UPDATE ? [p.targetId] : [],
        relatedConceptIds: p.type === INSIGHT_TYPE.ASSET_UPDATE ? [p.targetId] : [],
        tags: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        resolvedAt: null,
        metadata: {
          provider: llmProvider,
          model: llmModel || getProviderModel(llmProvider),
          finishReason: healthCheckResult.finishReason,
          generatedBy: 'ai',
        },
      }));

      await Promise.all(newInsightsData.map(data => addDoc(collection(db, 'insights'), data)));
      alert(`${proposals.length}件の更新候補が見つかりました。`);

    } catch (error) {
      console.error('Failed to run health check:', error);
      Sentry.captureException(error, { tags: { action: 'handleRunHealthCheck' } });
      if (error.message.includes('402') || error.code === 'insufficient_credits') {
        setCreditLimitMessage(error.message);
        setIsCreditLimitModalOpen(true);
      } else {
        alert(`ヘルスチェックに失敗しました: ${error.message}`);
      }
    } finally {
      setIsAuditing(false);
    }
  };

  const handleReSummarize = async (note) => {
    if (!note || isSummarizing) return;

    const roomId = note.sourceRoomIds?.[0];
    if (!roomId) {
      alert('元のチャットルームが見つかりません。');
      return;
    }

    const room = chatRooms.find(r => r.id === roomId);

    await handleRunMemoization(null, {
      saveMode: 'overwrite',
      hint: note.metadata?.memoHint || '',
      roomToUse: room || { id: roomId },
      noteToOverwrite: note,
    });
  };

  const handleExtractInsightsByTag = async () => {
    if (globalSelectedTags.length === 0 || notesForSelectedTag.length === 0 || isCrystallizing) return;

    if (!checkAiAvailability()) {
      openSettings();
      alert('APIキーが設定されていないため、Hint一括抽出を利用できません。Settingsから設定してください。');
      return;
    }

    // Pattern C check
    if (CREDIT_UX_PATTERN === 'C' && userData?.role === 'credit_beta' && userData.usedCredits >= userData.grantedCredits * CREDIT_WARNING_THRESHOLD) {
      setCreditLimitMessage('クレジットが80%に達したため、Hint一括抽出は制限されています。チャットは継続可能です。');
      setIsCreditLimitModalOpen(true);
      return;
    }

    const sourceNoteIds = notesForSelectedTag.map((note) => note.id);
    const sourceRoomIds = Array.from(new Set(notesForSelectedTag.flatMap((note) => note.sourceRoomIds || [])));
    const sourceMessageIds = Array.from(new Set(notesForSelectedTag.flatMap((note) => note.sourceMessageIds || [])));
    const relatedInsights = insights.filter((insight) => globalSelectedTags.some((tag) => (insight.tags || []).includes(tag)));
    const relatedAssets = concepts.filter((asset) => globalSelectedTags.some((tag) => (asset.tags || []).includes(tag)));

    if (IS_MOCK_DEMO) {
      try {
        setIsCrystallizing(true);
        const now = new Date().toISOString();
        const newInsight = {
          id: createMockId('mock-insight'),
          title: `${globalSelectedTags.map(t => '#' + t).join(', ')} から見える共通パターン`,
          summary: `${notesForSelectedTag.length}件のNoteをまたいで横断分析を行いました。`,
          type: 'synthesis_suggestion',
          suggestedTargetType: ASSET_TYPE.FRAMEWORK,
          status: INSIGHT_STATUS.SUGGESTED,
          confidence: 0.72,
          sourceNoteIds,
          sourceRoomIds,
          sourceMessageIds,
          relatedConceptIds: relatedAssets.map((asset) => asset.id),
          tags: [...globalSelectedTags],
          createdAt: now,
          updatedAt: now,
          resolvedAt: null,
          metadata: { provider: 'mock', model: 'demo', finishReason: 'completed', extractionScope: 'tag' },
        };

        setInsights((current) => [newInsight, ...current]);
        setNotes((current) => current.map((note) => (
          sourceNoteIds.includes(note.id)
            ? { ...note, relatedInsightIds: Array.from(new Set([...(note.relatedInsightIds || []), newInsight.id])), status: note.status === NOTE_STATUS.ARCHIVED ? note.status : NOTE_STATUS.LINKED, updatedAt: now }
            : note
        )));
        setSelectedInsightSourceIds([newInsight.id]);
        setSelectedInsightSourceNoteId(null);
        setSelectedInsightStatus('all');
        setSelectedInsightId(null);
        setSelectedAssetId(null);
        openSyncWorkspace('insights');
        showToast('1件のHintを追加しました');
        return;
      } finally {
        setIsCrystallizing(false);
      }
    }

    try {
      setIsCrystallizing(true);
      const result = await generateText({
        provider: llmProvider,
        apiKey: getCurrentApiKey(),
        model: getModelForAction(ACTION_TYPE.TAG_EXTRACT_INSIGHT),
        userRole: userData?.role,
        userData: userData,
        action_type: ACTION_TYPE.TAG_EXTRACT_INSIGHT,
        metadata: { roomId: null, relatedAssetIds: [], relatedNoteIds: sourceNoteIds, relatedInsightIds: [] },
        instructions: 'あなたはSecond Brainの横断分析エージェントです。',
        prompt: buildTagInsightPrompt({ tag: globalSelectedTags.join(', '), notes: notesForSelectedTag, relatedInsights, relatedAssets }),
        maxOutputTokens: INSIGHT_OUTPUT_TOKENS,
        timeoutMs: INSIGHT_TIMEOUT_MS,
      });
      let parsed;
      try { parsed = extractJsonObject(result.text); } catch { parsed = buildFallbackInsight(result.text); }
      const candidates = Array.isArray(parsed.insights) ? parsed.insights.slice(0, 5) : [];

      if (candidates.length === 0) {
        showToast('選択中のNoteは短期メモ寄りのため、新しいHintを作らずそのまま残す候補でした。', 'info');
        return;
      }

      const newInsightsData = candidates.map(candidate => ({
        ownerUid: user?.uid,
        title: candidate.title || 'Untitled Hint',
        summary: candidate.summary || '',
        type: candidate.type || 'synthesis_suggestion',
        suggestedTargetType: normalizeAssetType(candidate.suggestedTargetType),
        status: INSIGHT_STATUS.SUGGESTED,
        confidence: Number(candidate.confidence) || 0.5,
        sourceNoteIds,
        sourceRoomIds,
        sourceMessageIds,
        relatedConceptIds: relatedAssets.map((asset) => asset.id),
        tags: sanitizeTags(Array.from(new Set([...globalSelectedTags, ...(Array.isArray(candidate.tags) ? candidate.tags : [])]))),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        resolvedAt: null,
        metadata: {
          provider: llmProvider,
          model: llmModel || getProviderModel(llmProvider),
          finishReason: result.finishReason,
          extractionScope: 'tag',
          parseFailed: !!candidate.metadata?.parseFailed,
          rawOutput: candidate.metadata?.rawOutput || null,
        },
      }));
      const insightRefs = await Promise.all(newInsightsData.map(data => addDoc(collection(db, 'insights'), data)));
      if (!IS_MOCK_DEMO) {
        await Promise.all(insightRefs.map((ref, index) => syncNormalizedTags(db, user?.uid, ref.id, 'insight', newInsightsData[index].tags)));
      }

      await Promise.all(sourceNoteIds.map((noteId) => updateDoc(doc(db, 'notes', noteId), {
        relatedInsightIds: arrayUnion(...insightRefs.map((insightRef) => insightRef.id)),
        status: NOTE_STATUS.LINKED,
        updatedAt: serverTimestamp(),
      })));

      setSelectedInsightSourceIds(insightRefs.map((insightRef) => insightRef.id));
      setSelectedInsightSourceNoteId(null);
      setSelectedInsightStatus('all');
      setSelectedInsightId(null);
      setSelectedAssetId(null);
      openSyncWorkspace('insights');
      showToast(`${insightRefs.length}件のHintを追加しました`);
    } catch (error) {
      console.error('Failed to extract insights by tag:', error);
      Sentry.captureException(error, {
        tags: { action: 'handleExtractInsightsByTag' }
      });
      if (error.message.includes('402') || error.code === 'insufficient_credits' || error.code === 'heavy_action_blocked') {
        setCreditLimitMessage(error.message);
        setIsCreditLimitModalOpen(true);
      } else {
        alert(`タグ単位のHint抽出に失敗しました: ${error.message}`);
      }
    } finally {
      setIsCrystallizing(false);
    }
  };

  const handleAcceptProposal = async (insight) => {
    if (!insight || isAcceptingProposal) return;
    if (!isProposedChangeInsight(insight)) {
      alert('通常のHintは整理提案として適用できません。Asset化フローを使ってください。');
      return;
    }

    const whitelist = {
      note: ['title', 'body', 'summary', 'tags', 'status'],
      asset: ['title', 'body', 'type', 'tags', 'status', 'maturity']
    };

    const filterPatch = (patch, type) => {
      const allowed = whitelist[type] || [];
      const filtered = {};
      allowed.forEach(key => {
        if (Object.prototype.hasOwnProperty.call(patch, key)) {
          filtered[key] = patch[key];
        }
      });
      return filtered;
    };

    const getTagMergePatch = (patch) => ({
      oldTag: patch.oldTag ?? patch.from,
      newTag: patch.newTag ?? patch.to,
    });

    if (IS_MOCK_DEMO) {
      try {
        setIsAcceptingProposal(true);
        const patch = insight.proposedPatch || {};
        if (insight.type === INSIGHT_TYPE.NOTE_UPDATE) {
          const filtered = filterPatch(patch, 'note');
          setNotes(current => current.map(n => n.id === insight.targetId ? { ...n, ...filtered, updatedAt: new Date().toISOString(), metadata: { ...n.metadata, generatedBy: 'ai' } } : n));
        } else if (insight.type === INSIGHT_TYPE.ASSET_UPDATE) {
          const filtered = filterPatch(patch, 'asset');
          setConcepts(current => current.map(c => c.id === insight.targetId ? { ...c, ...filtered, updatedAt: new Date().toISOString(), metadata: { ...c.metadata, generatedBy: 'ai' } } : c));
        } else if (insight.type === INSIGHT_TYPE.TAG_MERGE) {
          const { oldTag, newTag } = getTagMergePatch(patch);
          if (oldTag && newTag) {
            setNotes(current => current.map(n => n.tags?.includes(oldTag) ? { ...n, tags: Array.from(new Set(n.tags.map(t => t === oldTag ? newTag : t))), updatedAt: new Date().toISOString() } : n));
            setConcepts(current => current.map(c => c.tags?.includes(oldTag) ? { ...c, tags: Array.from(new Set(c.tags.map(t => t === oldTag ? newTag : t))), updatedAt: new Date().toISOString() } : c));
            setInsights(current => current.map(i => i.tags?.includes(oldTag) ? { ...i, tags: Array.from(new Set(i.tags.map(t => t === oldTag ? newTag : t))), updatedAt: new Date().toISOString() } : i));
          }
        } else if (insight.type?.startsWith('duplicate')) {
          throw new Error('重複整理提案は専用の確認画面で処理してください。');
        } else {
          throw new Error(`Unsupported proposal type: ${insight.type}`);
        }
        setInsights(current => current.map(i => i.id === insight.id ? { ...i, status: INSIGHT_STATUS.ACCEPTED, updatedAt: new Date().toISOString(), resolvedAt: new Date().toISOString() } : i));
        setSelectedInsightId(null);
        alert('提案を適用しました（デモ）。');
      } finally {
        setIsAcceptingProposal(false);
      }
      return;
    }

    try {
      setIsAcceptingProposal(true);
      const patch = insight.proposedPatch || {};
      const acceptedFields = {
        status: INSIGHT_STATUS.ACCEPTED,
        updatedAt: serverTimestamp(),
        resolvedAt: serverTimestamp(),
      };
      let proposalStatusUpdated = false;

      if (insight.type === INSIGHT_TYPE.TAG_MERGE) {
        const { oldTag, newTag } = getTagMergePatch(patch);
        if (!oldTag || !newTag) throw new Error('Invalid tag merge patch');

        const batch = writeBatch(db);
        const collectionsToUpdate = ['notes', 'concepts', 'insights'];
        const tagSyncTasks = [];

        for (const colName of collectionsToUpdate) {
          const q = query(collection(db, colName), where('ownerUid', '==', user.uid), where('tags', 'array-contains', oldTag));
          const snapshot = await getDocs(q);

          snapshot.docs.forEach((d) => {
            const data = d.data();
            const previousTags = data.tags || [];
            const newTags = Array.from(new Set(previousTags.map(t => t === oldTag ? newTag : t)));

            const updatePayload = {
              tags: newTags,
              updatedAt: serverTimestamp()
            };
            if (colName === 'insights' && d.id === insight.id) {
              Object.assign(updatePayload, acceptedFields);
              proposalStatusUpdated = true;
            }
            batch.update(d.ref, updatePayload);

            let docType = 'note';
            if (colName === 'concepts') docType = 'concept';
            if (colName === 'insights') docType = 'insight';
            tagSyncTasks.push({ id: d.id, type: docType, next: newTags, prev: previousTags });
          });
        }
        if (!proposalStatusUpdated) {
          batch.update(doc(db, 'insights', insight.id), acceptedFields);
          proposalStatusUpdated = true;
        }
        await batch.commit();
        // Tag normalization is side-effect, run after batch
        await Promise.all(tagSyncTasks.map(task => syncNormalizedTags(db, user.uid, task.id, task.type, task.next, task.prev)));

      } else if (insight.type === INSIGHT_TYPE.DUPLICATE_NOTE || insight.type === INSIGHT_TYPE.DUPLICATE_ASSET || insight.type === INSIGHT_TYPE.DUPLICATE_INSIGHT) {
        await handleAcceptDuplicateProposal(insight);
        return;
      } else if (insight.type === INSIGHT_TYPE.NOTE_UPDATE || insight.type === INSIGHT_TYPE.ASSET_UPDATE) {
        const isNote = insight.type === INSIGHT_TYPE.NOTE_UPDATE;
        const collectionName = isNote ? 'notes' : 'concepts';
        const filteredPatch = filterPatch(patch, isNote ? 'note' : 'asset');

        const docRef = doc(db, collectionName, insight.targetId);
        const targetDoc = await getDocs(query(collection(db, collectionName), where('ownerUid', '==', user.uid), where('__name__', '==', insight.targetId)));

        if (targetDoc.empty) throw new Error('Target document not found or access denied');
        const docSnap = targetDoc.docs[0];

        const previousTags = docSnap.data().tags || [];
        const batch = writeBatch(db);
        batch.update(docRef, {
          ...filteredPatch,
          updatedAt: serverTimestamp(),
          'metadata.generatedBy': 'ai',
        });
        batch.update(doc(db, 'insights', insight.id), acceptedFields);
        await batch.commit();
        proposalStatusUpdated = true;

        if (filteredPatch.tags) {
          let docType = isNote ? 'note' : 'concept';
          await syncNormalizedTags(db, user.uid, docSnap.id, docType, filteredPatch.tags, previousTags);
        }
      } else {
        throw new Error(`Unsupported proposal type: ${insight.type}`);
      }

      if (!proposalStatusUpdated) {
        await updateDoc(doc(db, 'insights', insight.id), acceptedFields);
      }

      setSelectedInsightId(null);
      alert('提案を適用しました。');
    } catch (error) {
      console.error('Failed to accept proposal:', error);
      Sentry.captureException(error, { tags: { action: 'handleAcceptProposal' }, extra: { insightId: insight.id } });
      alert(`適用に失敗しました: ${error.message}`);
    } finally {
      setIsAcceptingProposal(false);
    }
  };

  const handleMergeInsightIntoAsset = async (insight, targetAsset) => {
    if (!insight || !targetAsset) return;

    if (!window.confirm(`Hint「${insight.title}」を既存のAsset「${targetAsset.title}」に紐付けますか？`)) return;

    try {
      setIsAssetTypeModalOpen(false);
      setAssetizingInsight(null);
      setSelectedInsightId(null);

      if (IS_MOCK_DEMO) {
        const now = new Date().toISOString();
        setInsights(current => current.map(i => i.id === insight.id ? {
          ...i,
          status: INSIGHT_STATUS.ACCEPTED,
          conceptId: targetAsset.id,
          relatedConceptIds: Array.from(new Set([...(i.relatedConceptIds || []), targetAsset.id])),
          updatedAt: now,
          resolvedAt: now
        } : i));
        setConcepts(current => current.map(c => c.id === targetAsset.id ? {
          ...c,
          sourceInsightIds: Array.from(new Set([...(c.sourceInsightIds || []), insight.id])),
          sourceNoteIds: Array.from(new Set([...(c.sourceNoteIds || []), ...(insight.sourceNoteIds || [])])),
          updatedAt: now
        } : c));
      } else {
        await updateDoc(doc(db, 'insights', insight.id), {
          status: INSIGHT_STATUS.ACCEPTED,
          conceptId: targetAsset.id,
          relatedConceptIds: arrayUnion(targetAsset.id),
          updatedAt: serverTimestamp(),
          resolvedAt: serverTimestamp()
        });
        await updateDoc(doc(db, 'concepts', targetAsset.id), {
          sourceInsightIds: arrayUnion(insight.id),
          sourceNoteIds: arrayUnion(...(insight.sourceNoteIds || [])),
          updatedAt: serverTimestamp()
        });
      }
      alert('Assetに紐付けました。');
    } catch (error) {
      console.error('Failed to merge insight into asset:', error);
      alert('紐付けに失敗しました。');
    }
  };

  const handlePromoteInsightToConcept = async (e) => {
    e.preventDefault();
    const insight = assetizingInsight;
    if (!insight) return;

    try {
      const assetType = normalizeAssetType(assetTypeDraft);
      if (IS_MOCK_DEMO) {
        const now = new Date().toISOString();
        const conceptId = createMockId('mock-concept');
        const concept = {
          id: conceptId,
          title: assetTitleDraft || insight.title || 'Untitled Asset',
          body: assetBodyDraft || insight.summary || '',
          type: assetType,
          status: getInitialAssetStatus(assetType),
          maturity: ASSET_MATURITY.SEED,
          tags: insight.tags || [],
          sourceInsightIds: [insight.id],
          sourceNoteIds: insight.sourceNoteIds || [],
          sourceRoomIds: insight.sourceRoomIds || [],
          createdAt: now,
          updatedAt: now,
          usageStatus: ASSET_USAGE_STATUS.NEW,
          metadata: {
            confidence: insight.confidence || 0.5,
            promotedFrom: 'insight',
            generatedBy: 'mock',
            referenceCount: 0,
            referencesLast30Days: 0,
            lastReferencedAt: null,
            usageCalculatedAt: now,
            usageRuleVersion: ASSET_USAGE_RULE_VERSION,
          },
        };

        setConcepts((current) => [concept, ...current]);
        setInsights((current) => current.map((item) => (
          item.id === insight.id ? { ...item, status: INSIGHT_STATUS.ACCEPTED, conceptId: conceptId, suggestedTargetType: assetType, relatedConceptIds: [...(item.relatedConceptIds || []), conceptId], updatedAt: now, resolvedAt: now } : item
        )));
        setNotes((current) => current.map((note) => (
          (insight.sourceNoteIds || []).includes(note.id) ? { ...note, relatedConceptIds: Array.from(new Set([...(note.relatedConceptIds || []), conceptId])), updatedAt: now } : note
        )));

        const roomIdsToUpdate = insight.sourceRoomIds || (insight.sourceRoomId ? [insight.sourceRoomId] : []);
        setChatRooms(current => current.map(room => roomIdsToUpdate.includes(room.id) ? { ...room, status: CHAT_ROOM_STATUS.ASSETIZED, updatedAt: now } : room));

        setIsAssetTypeModalOpen(false);
        setAssetizingInsight(null);
        setSelectedInsightId(null);
        return;
      }

      setIsAssetTypeModalOpen(false);
      setAssetizingInsight(null);
      setSelectedInsightId(null);

      const conceptData = {
        ownerUid: user?.uid,
        title: assetTitleDraft || insight.title || 'Untitled Asset',
        body: assetBodyDraft || insight.summary || '',
        type: assetType,
        status: getInitialAssetStatus(assetType),
        maturity: ASSET_MATURITY.SEED,
        tags: insight.tags || [],
        sourceInsightIds: [insight.id],
        sourceNoteIds: insight.sourceNoteIds || [],
        sourceRoomIds: insight.sourceRoomIds || [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        usageStatus: ASSET_USAGE_STATUS.NEW,
        metadata: {
          confidence: insight.confidence || 0.5,
          promotedFrom: 'insight',
          referenceCount: 0,
          referencesLast30Days: 0,
          lastReferencedAt: null,
          usageCalculatedAt: serverTimestamp(),
          usageRuleVersion: ASSET_USAGE_RULE_VERSION,
        },
      };
      const conceptRef = await addDoc(collection(db, 'concepts'), conceptData);
      if (!IS_MOCK_DEMO) await syncNormalizedTags(db, user?.uid, conceptRef.id, 'concept', conceptData.tags);

      await updateDoc(doc(db, 'insights', insight.id), {
        status: INSIGHT_STATUS.ACCEPTED,
        conceptId: conceptRef.id,
        suggestedTargetType: assetType,
        relatedConceptIds: [...(insight.relatedConceptIds || []), conceptRef.id],
        updatedAt: serverTimestamp(),
        resolvedAt: serverTimestamp(),
      });

      const roomIdsToUpdate = insight.sourceRoomIds || (insight.sourceRoomId ? [insight.sourceRoomId] : []);
      await Promise.all(roomIdsToUpdate.map(roomId =>
        updateDoc(doc(db, 'chatRooms', roomId), { status: CHAT_ROOM_STATUS.ASSETIZED, updatedAt: serverTimestamp() })
      ));
    } catch (error) {
      console.error('Failed to promote insight:', error);
      Sentry.captureException(error, {
        tags: { action: 'handlePromoteInsightToConcept' },
        extra: { insightId: insight.id }
      });
      alert(`Asset化に失敗しました: ${error.message}`);
    }
  };

  const openSourceNote = (noteId) => {
    if (!noteId) return;
    setSelectedNoteId(noteId);
    openSinkNotes();
  };

  const openSourceRoom = (roomId, messageIds = []) => {
    if (!roomId) return;
    setSinkRoomId(roomId);
    setHighlightedRawMessageIds(messageIds || []);
    if (!IS_MOCK_DEMO) { localStorage.setItem(`secondBrain.${user?.uid}.sinkRoomId`, roomId); }
    openSinkInput();
  };

  const openRawSource = (note) => {
    if (!note) return;
    openSourceRoom(note.sourceRoomIds?.[0], note.sourceMessageIds || []);
  };

  const getDisplayEntityType = (type) => {
    if (type === 'insight') return 'Hint';
    if (type === 'asset') return 'Asset';
    if (type === 'note') return 'Note';
    if (type === 'raw') return 'Memo';
    return type;
  };

  const healthStats = useMemo(() => {
    const pendingSummaries = chatRoomsWithWorkflow.filter((room) => room.workflow?.requiresMemo).length;
    const suggestedHints = pendingHintCount;
    const proposedChanges = insights.filter(i =>
      i.status === INSIGHT_STATUS.SUGGESTED &&
      isProposedChangeInsight(i) &&
      i.type !== INSIGHT_TYPE.MISSING_LINK &&
      i.type !== INSIGHT_TYPE.TAG_MERGE
    ).length;
    const missingLinks = insights.filter(i => i.status === INSIGHT_STATUS.SUGGESTED && i.type === INSIGHT_TYPE.MISSING_LINK).length;
    const tagMergeSuggestions = insights.filter(i => i.status === INSIGHT_STATUS.SUGGESTED && i.type === INSIGHT_TYPE.TAG_MERGE).length;
    const activeAssets = concepts.filter(c => c.status !== ASSET_STATUS.ARCHIVED && c.status !== ASSET_STATUS.DEPRECATED);
    const reviewedAssets = activeAssets.map((asset) => {
      const usage = deriveAssetUsageMetrics(asset);
      return { ...asset, ...usage };
    });
    const reviewAssets = reviewedAssets.filter(asset => asset.usageStatus === ASSET_USAGE_STATUS.COOLING).length;
    const dormantAssets = reviewedAssets.filter(asset => asset.usageStatus === ASSET_USAGE_STATUS.DORMANT).length;
    const archiveCandidates = reviewedAssets.filter(asset => (
      asset.usageStatus === ASSET_USAGE_STATUS.DORMANT &&
      isArchiveCandidate(asset)
    )).length;

    return {
      pendingSummaries,
      suggestedHints,
      proposedChanges,
      missingLinks,
      tagMergeSuggestions,
      reviewAssets,
      dormantAssets,
      archiveCandidates,
    };
  }, [chatRoomsWithWorkflow, insights, pendingHintCount, concepts]);

  const totalHealthIssues = useMemo(() => (
    healthStats.pendingSummaries +
    healthStats.suggestedHints +
    healthStats.proposedChanges +
    healthStats.missingLinks
  ), [healthStats]);

  const showFirstUseFlow = useMemo(() => (
    notes.length === 0 && insights.length === 0 && concepts.length === 0
  ), [notes.length, insights.length, concepts.length]);

  const handleOpenCurrentSummaryNote = () => {
    if (!currentRoom?.summaryNoteId) return;
    setSelectedNoteId(currentRoom.summaryNoteId);
    openSyncWorkspace('notes');
  };

  const renderSyncWorkflowStatus = () => (
    <div className="border-b border-gray-800 bg-gray-950/70 px-3 py-2 shrink-0">
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded-full border border-blue-800/50 bg-blue-950/40 px-2 py-1 text-[10px] font-bold text-blue-200">
          Noteまとめ待ち {healthStats.pendingSummaries}件
        </span>
        <span className="rounded-full border border-purple-800/50 bg-purple-950/40 px-2 py-1 text-[10px] font-bold text-purple-200">
          未処理Hint {healthStats.suggestedHints}件
        </span>
      </div>
      <p className="mt-2 text-[11px] leading-relaxed text-gray-400">
        {healthStats.suggestedHints > 0
          ? 'Hintは全部すぐに処理しなくてOKです。気になるものだけAsset化し、迷うものは残したままで進められます。'
          : '未処理Hintは0件です。0件を維持し続ける必要はなく、必要になった時だけ次のHintを作ればOKです。'}
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        {healthStats.pendingSummaries > 0 && (
          <button
            type="button"
            onClick={() => {
              setActiveTab('input');
              openChatHistoryPending();
            }}
            className="rounded-lg border border-blue-700/50 bg-blue-950/30 px-2.5 py-1.5 text-[10px] font-bold text-blue-200 transition-colors hover:bg-blue-900/40"
          >
            Sinkでまとめ待ちを見る
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            setSelectedNoteId(null);
            setSelectedInsightId(null);
            setSelectedAssetId(null);
            openSyncWorkspace('insights');
            setSelectedInsightStatus(INSIGHT_STATUS.SUGGESTED);
            setSelectedInsightKind('hint');
            setSelectedInsightType('all');
          }}
          className="rounded-lg border border-purple-700/50 bg-purple-950/30 px-2.5 py-1.5 text-[10px] font-bold text-purple-200 transition-colors hover:bg-purple-900/40"
        >
          {healthStats.suggestedHints > 0 ? 'Hintを確認する' : 'Hint一覧を開く'}
        </button>
      </div>
    </div>
  );

  const handleAcceptDuplicateProposal = async (insight) => {
    if (isProcessingDuplicate || !insight.proposedPatch) return;
    const { mergeFrom } = insight.proposedPatch;
    const targetType = insight.targetType;

    setIsProcessingDuplicate(true);
    try {
      if (targetType === 'note') {
        await handleUpdateNoteStatus(mergeFrom, NOTE_STATUS.ARCHIVED);
      } else if (targetType === 'asset') {
        await handleUpdateAssetStatus(mergeFrom, ASSET_STATUS.ARCHIVED);
      } else if (targetType === 'insight') {
        if (IS_MOCK_DEMO) {
          setInsights(prev => prev.map(i => i.id === mergeFrom ? { ...i, status: INSIGHT_STATUS.REJECTED, updatedAt: new Date().toISOString(), resolvedAt: new Date().toISOString(), rejectionMemo: '重複のため却下' } : i));
        } else {
          await updateDoc(doc(db, 'insights', mergeFrom), {
            status: INSIGHT_STATUS.REJECTED,
            updatedAt: serverTimestamp(),
            resolvedAt: serverTimestamp(),
            rejectionMemo: '重複のため却下'
          });
        }
      }

      if (IS_MOCK_DEMO) {
        setInsights(prev => prev.map(i => i.id === insight.id ? { ...i, status: INSIGHT_STATUS.ACCEPTED, resolvedAt: new Date().toISOString() } : i));
      } else {
        await updateDoc(doc(db, 'insights', insight.id), {
          status: INSIGHT_STATUS.ACCEPTED,
          updatedAt: serverTimestamp(),
          resolvedAt: serverTimestamp()
        });
      }
      alert('提案を受理しました。');
      setSelectedInsightId(null);
    } catch (error) {
      console.error('Failed to accept duplicate proposal:', error);
      alert('エラーが発生しました。');
    } finally {
      setIsProcessingDuplicate(false);
    }
  };

  const handleRunDuplicateCheck = async () => {
    if (isHealthChecking) return;
    setIsHealthChecking(true);
    try {
      // Phase A: Duplicate detection
      const noteDuplicates = detectDuplicateNotes(notes);
      const assetDuplicates = detectDuplicateAssets(concepts);
      const insightDuplicates = detectDuplicateInsights(insights);
      const tagMergeCandidates = detectTagMergeCandidates({ notes, concepts, insights });
      const allProposals = [...noteDuplicates, ...assetDuplicates, ...insightDuplicates, ...tagMergeCandidates];

      // Filter out proposals that are already suggested
      const existingProposalKeys = new Set(
        insights
          .filter(i => i.status === INSIGHT_STATUS.SUGGESTED && (i.type?.startsWith('duplicate') || i.type === INSIGHT_TYPE.TAG_MERGE))
          .map(i => `${i.type}:${i.title}`)
      );
      const newProposals = allProposals.filter(p => !existingProposalKeys.has(`${p.type}:${p.title}`));

      if (newProposals.length === 0) {
        alert('新たな整理候補は見つかりませんでした。');
        return;
      }

      if (IS_MOCK_DEMO) {
        const now = new Date().toISOString();
        const mockInsights = newProposals.map(d => ({
          id: createMockId('mock-insight'),
          ...d,
          kind: 'proposed_change',
          status: INSIGHT_STATUS.SUGGESTED,
          createdAt: now,
          updatedAt: now,
          ownerUid: user?.uid,
          metadata: { generatedBy: 'health-check-phase-a' }
        }));
        setInsights(prev => [...mockInsights, ...prev]);
      } else {
        await Promise.all(newProposals.map(d => addDoc(collection(db, 'insights'), {
          ...d,
          kind: 'proposed_change',
          status: INSIGHT_STATUS.SUGGESTED,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          ownerUid: user?.uid,
          metadata: { generatedBy: 'health-check-phase-a' }
        })));
      }
      alert(`${newProposals.length}件の整理候補が見つかりました。Sync > Hint一覧で確認してください。`);
    } catch (error) {
      console.error('Health check failed:', error);
      alert('ヘルスチェックに失敗しました。');
    } finally {
      setIsHealthChecking(false);
    }
  };

  return (
    <div
      className="bg-gray-900 text-gray-200 font-sans flex flex-col overflow-hidden w-full"
      style={{
        position: isLargeScreen ? 'relative' : 'fixed',
        top: isLargeScreen ? 0 : `${viewportTop}px`,
        left: 0,
        right: 0,
        height: isLargeScreen ? '100dvh' : viewportHeight,
        paddingTop: isStandalonePWA && !isLargeScreen ? 'env(safe-area-inset-top)' : '0px',
      }}
    >
      {/* Main Content */}
      <main className="flex-1 overflow-hidden relative">
        {isLargeScreen ? (
          <PanelGroup direction="horizontal" className="h-full">
            <Panel defaultSize={25} minSize={15}>
              <div className="h-full flex flex-col bg-gray-900 overflow-hidden border-r border-gray-800 relative">
            {isGuideOpen && guideStepIndex === 0 && (
              <div className="absolute inset-0 z-[60] pointer-events-none ring-4 ring-indigo-500 ring-inset shadow-[0_0_50px_rgba(99,102,241,0.3)] animate-pulse-slow transition-all duration-500" />
            )}
            
            {sinkViewMode !== 'input' && (
              <div className="flex items-center justify-between p-3 border-b border-gray-800 bg-gray-900/80 shrink-0 z-20 h-12 transition-all duration-500">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-black text-white uppercase tracking-widest">Sink</span>
                </div>
                <div className="flex bg-gray-950 rounded-full p-0.5 border border-gray-800 ml-2 overflow-x-auto no-scrollbar shrink-0">
                  <button onClick={() => setInputMode('input')} className={`px-3 py-1 rounded-full text-xs font-bold transition-all active:scale-95 whitespace-nowrap ${inputMode === 'input' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-gray-300'}`}>Sink</button>
                  <button onClick={() => setInputMode('discuss')} className={`px-3 py-1 rounded-full text-xs font-bold transition-all active:scale-95 whitespace-nowrap ${inputMode === 'discuss' ? 'bg-indigo-600 text-white' : 'text-gray-400 hover:text-gray-300'}`}>Discuss</button>
                  <button onClick={() => setInputMode('anti')} className={`px-3 py-1 rounded-full text-xs font-bold transition-all active:scale-95 whitespace-nowrap ${inputMode === 'anti' ? 'bg-rose-600 text-white' : 'text-gray-400 hover:text-gray-300'}`}>Anti</button>
                </div>
              </div>
            )}

            {sinkViewMode === 'input' ? (
              <SinkChatPane
                sinkMessages={sinkMessages}
                sinkMessagesEndRef={sinkMessagesEndRef}
                sinkScrollContainerRef={sinkScrollContainerRef}
                sinkRoomScrollPositions={sinkRoomScrollPositions}
                sinkRoomId={sinkRoomId}
                currentRoom={currentRoom}
                scrollToBottom={scrollToBottom}
                highlightedRawMessageIds={highlightedRawMessageIds}
                concepts={concepts}
                notes={notes}
                insights={insights}
                getAssetTypeLabel={getAssetTypeLabel}
                onReferenceClick={handleReferenceClick}
                openMemoModal={openMemoModal}
                isSummarizing={isSummarizing}
                isSaving={isSaving}
                isLargeScreen={isLargeScreen}
                sinkInputText={sinkInputText}
                setSinkInputText={handleSinkInputTextChange}
                sinkDraftMetadata={sinkDraftMetadata}
                onLongPasteDetected={handleLongPasteDetected}
                longPasteCharThreshold={LONG_PASTE_CHAR_THRESHOLD}
                sinkInputCharLimit={SINK_INPUT_CHAR_LIMIT}
                aiActiveInputCharLimit={AI_ACTIVE_INPUT_CHAR_LIMIT}
                currentRoomWorkflow={currentRoom?.workflow || null}
                showFirstUseFlow={showFirstUseFlow}
                onOpenSummaryNote={handleOpenCurrentSummaryNote}
                inputMode={inputMode}
                setInputMode={setInputMode}
                setHistoryIndex={setHistoryIndex}
                handleInputKeyDown={handleInputKeyDown}
                handleSave={handleSave}
                debugMode={debugMode}
                handleResend={handleResend}
                openSettings={openSettings}
                isSinkRoomNearLimit={isSinkRoomNearLimit}
                isSinkRoomAtLimit={isSinkRoomAtLimit}
                MAX_MESSAGES_PER_ROOM={MAX_MESSAGES_PER_ROOM}
                INPUT_MODE_DETAILS={INPUT_MODE_DETAILS}
                handleStartNewRoom={handleStartNewRoom}
                handleSummarizeAndNewRoom={handleSummarizeAndNewRoom}
                setIsChatListModalOpen={() => openChatHistory()}
                isDeepDiscussMode={isDeepDiscussMode}
                setIsDeepDiscussMode={setIsDeepDiscussMode}
                isMultilineMode={isMultilineMode}
                setIsMultilineMode={setIsMultilineMode}
                activeReferencedAssets={activeReferencedAssets}
                onRemoveAsset={handleRemoveIgnoredAsset}
                sinkContextTitle={(() => {
                  const room = chatRooms.find(r => r.id === sinkRoomId);
                  if (room?.contextRefs?.length > 0) return room.title;
                  if (pendingSinkContextRefs?.length > 0) return pendingSinkRoomTitle;
                  return null;
                })()}
              />
            ) : (
              <div className="flex-1 flex flex-col min-h-0 bg-gray-900 overflow-hidden">
                {!selectedNote ? (
                  <NoteList
                    notes={notes}
                    filteredNotes={filteredNotes}
                    setSelectedNoteId={setSelectedNoteId}
                    noteSearchText={noteSearchText}
                    setNoteSearchText={setNoteSearchText}
                    globalSelectedTags={globalSelectedTags}
                    setGlobalSelectedTags={setGlobalSelectedTags}
                    selectedNoteFilter={selectedNoteFilter}
                    setSelectedNoteFilter={setSelectedNoteFilter}
                    allAvailableTags={allAvailableTags}
                    notesForSelectedTag={notesForSelectedTag}
                    handleExtractInsightsByTag={handleExtractInsightsByTag}
                    handleReSummarize={handleReSummarize}
                    isSummarizing={isSummarizing}
                    isCrystallizing={isCrystallizing}
                    insights={insights}
                    handleUpdateNoteTags={handleUpdateNoteTags}
                    tagSuggestions={tagSuggestions}
                    showToast={showToast}
                    jumpToMap={jumpToMap}
                    isLargeScreen={isLargeScreen}
                    handleDiscussInSink={handleDiscussInSink}
                  />
                ) : (
                  <NoteDetail
                    selectedNote={selectedNote}
                    setSelectedNoteId={setSelectedNoteId}
                    handleCrystallizeNote={handleCrystallizeNote}
                    handleReSummarize={handleReSummarize}
                    isSummarizing={isSummarizing}
                    isCrystallizing={isCrystallizing}
                    selectedNoteInsightCount={selectedNoteInsightCount}
                    setSelectedInsightSourceNoteId={setSelectedInsightSourceNoteId}
                    setSelectedInsightType={setSelectedInsightType}
                    setSelectedInsightStatus={setSelectedInsightStatus}
                    setInsightSearchText={setInsightSearchText}
                    openSyncWorkspace={openSyncWorkspace}
                    handleUpdateNoteTags={handleUpdateNoteTags}
                    openRawSource={openRawSource}
                    jumpToMap={jumpToMap}
                    handleDiscussInSink={handleDiscussInSink}
                    insights={insights}
                    concepts={concepts}
                    setSelectedInsightId={setSelectedInsightId}
                    setSelectedAssetId={setSelectedAssetId}
                    tagSuggestions={tagSuggestions}
                    showToast={showToast}
                  />
                )}
              </div>
            )}
              </div>
            </Panel>
            <PanelResizeHandle className="w-1 bg-gray-700 hover:bg-indigo-500 transition-colors cursor-col-resize" />
            <Panel defaultSize={25} minSize={15}>
              <div className="h-full flex flex-col bg-gray-900 overflow-hidden border-r border-gray-800 relative">
                <div className="flex flex-col border-gray-800 bg-gray-900 flex-1 overflow-hidden relative">
              {isGuideOpen && guideStepIndex === 1 && (
                <div className="absolute inset-0 z-[60] pointer-events-none ring-4 ring-indigo-500 ring-inset shadow-[0_0_50px_rgba(99,102,241,0.3)] animate-pulse transition-all duration-500" />
              )}
              
              <div className={`flex items-center justify-between px-3 border-b border-gray-800 bg-gray-900/80 shrink-0 z-20 h-12 transition-colors ${!isLargeScreen ? 'cursor-pointer select-none hover:bg-gray-800/60' : ''}`} onClick={() => !isLargeScreen && handleToggleSyncTray()}>
                <div className="flex items-center gap-1">
                  <div className="relative">
                    <button
                      onClick={handleToggleHealthPanel}
                      className={`p-1.5 rounded-lg transition-colors shrink-0 ${isHealthPanelExpanded ? 'bg-gray-800 text-white' : 'text-gray-400 hover:bg-gray-800 hover:text-white'}`}
                      title="ナレッジ状態"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </button>
                    {totalHealthIssues > 0 && (
                      <span className="absolute top-0 right-0 bg-rose-600 w-2 h-2 rounded-full pointer-events-none" />
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                  {assetViewMode === 'notes' && !selectedNote && (
                    <div className="relative">
                      <button
                        onClick={(e) => { e.stopPropagation(); setIsNoteStatusFilterOpen(v => !v); }}
                        className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold border transition-all whitespace-nowrap ${
                          selectedNoteFilter !== 'all'
                            ? 'bg-blue-600/20 text-blue-300 border-blue-500/60'
                            : 'bg-gray-800/60 text-gray-400 border-gray-700 hover:text-gray-300'
                        }`}
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
                        </svg>
                        {{'unextracted':'Hint未抽出','extracted':'Hint抽出済','all':'すべて'}[selectedNoteFilter]}
                      </button>
                      {isNoteStatusFilterOpen && (
                        <>
                          <div className="fixed inset-0 z-40" onClick={() => setIsNoteStatusFilterOpen(false)} />
                          <div className="absolute right-0 top-full mt-1.5 bg-gray-800 border border-gray-700 rounded-xl shadow-2xl z-50 overflow-hidden min-w-[100px]">
                            {NOTE_STATUS_FILTER_OPTIONS.map(([filter, label]) => (
                              <button
                                key={filter}
                                onClick={(e) => { e.stopPropagation(); setSelectedNoteFilter(filter); setIsNoteStatusFilterOpen(false); }}
                                className={`w-full text-left px-3 py-2 text-[11px] font-bold transition-colors ${
                                  selectedNoteFilter === filter
                                    ? 'bg-blue-600/30 text-blue-300'
                                    : 'text-gray-300 hover:bg-gray-700'
                                }`}
                              >
                                {label}
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  {assetViewMode === 'insights' && !selectedInsight && (
                    <div className="relative">
                      <button
                        onClick={(e) => { e.stopPropagation(); setIsInsightStatusFilterOpen(v => !v); }}
                        className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold border transition-all whitespace-nowrap ${
                          selectedInsightStatus !== 'all'
                            ? 'bg-purple-600/20 text-purple-300 border-purple-500/60'
                            : 'bg-gray-800/60 text-gray-400 border-gray-700 hover:text-gray-300'
                        }`}
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
                        </svg>
                        {Object.fromEntries(INSIGHT_STATUS_FILTER_OPTIONS)[selectedInsightStatus]}
                      </button>
                      {isInsightStatusFilterOpen && (
                        <>
                          <div className="fixed inset-0 z-40" onClick={() => setIsInsightStatusFilterOpen(false)} />
                          <div className="absolute right-0 top-full mt-1.5 bg-gray-800 border border-gray-700 rounded-xl shadow-2xl z-50 overflow-hidden min-w-[100px]">
                            {INSIGHT_STATUS_FILTER_OPTIONS.map(([status, label]) => (
                              <button
                                key={status}
                                onClick={(e) => { e.stopPropagation(); setSelectedInsightStatus(status); setSelectedInsightType('all'); setIsInsightStatusFilterOpen(false); }}
                                className={`w-full text-left px-3 py-2 text-[11px] font-bold transition-colors ${
                                  selectedInsightStatus === status
                                    ? 'bg-purple-600/30 text-purple-300'
                                    : 'text-gray-300 hover:bg-gray-700'
                                }`}
                              >
                                {label}
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  {assetViewMode === 'assets' && !selectedAsset && (
                    <div className="relative">
                      <button
                        onClick={(e) => { e.stopPropagation(); setIsAssetStatusFilterOpen(v => !v); }}
                        className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold border transition-all whitespace-nowrap ${
                          selectedAssetStatus !== 'all'
                            ? 'bg-emerald-600/20 text-emerald-300 border-emerald-500/60'
                            : 'bg-gray-800/60 text-gray-400 border-gray-700 hover:text-gray-300'
                        }`}
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
                        </svg>
                        {Object.fromEntries(ASSET_STATUS_FILTER_OPTIONS)[selectedAssetStatus]}
                      </button>
                      {isAssetStatusFilterOpen && (
                        <>
                          <div className="fixed inset-0 z-40" onClick={() => setIsAssetStatusFilterOpen(false)} />
                          <div className="absolute right-0 top-full mt-1.5 bg-gray-800 border border-gray-700 rounded-xl shadow-2xl z-50 overflow-hidden min-w-[100px]">
                            {ASSET_STATUS_FILTER_OPTIONS.map(([status, label]) => (
                              <button
                                key={status}
                                onClick={(e) => { e.stopPropagation(); setSelectedAssetStatus(status); setIsAssetStatusFilterOpen(false); }}
                                className={`w-full text-left px-3 py-2 text-[11px] font-bold transition-colors ${
                                  selectedAssetStatus === status
                                    ? 'bg-emerald-600/30 text-emerald-300'
                                    : 'text-gray-300 hover:bg-gray-700'
                                }`}
                              >
                                {label}
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  <div className="flex bg-gray-950 rounded-full p-0.5 border border-gray-800 overflow-x-auto no-scrollbar shrink-0">
                    <button onClick={(e) => { e.stopPropagation(); setSelectedNoteId(null); setSelectedInsightId(null); setSelectedAssetId(null); setAssetViewMode('notes'); setIsSyncTrayExpanded(true); }} className={`px-3 py-1 rounded-full text-[10px] font-bold transition-all active:scale-95 whitespace-nowrap ${assetViewMode === 'notes' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-gray-300'}`}>Note</button>
                    <button onClick={(e) => { e.stopPropagation(); setSelectedNoteId(null); setSelectedInsightId(null); setSelectedAssetId(null); setAssetViewMode('insights'); setIsSyncTrayExpanded(true); }} className={`px-3 py-1 rounded-full text-[10px] font-bold transition-all active:scale-95 whitespace-nowrap ${assetViewMode === 'insights' ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-gray-300'}`}>Hint</button>
                    <button onClick={(e) => { e.stopPropagation(); setSelectedNoteId(null); setSelectedInsightId(null); setSelectedAssetId(null); setAssetViewMode('assets'); setIsSyncTrayExpanded(true); }} className={`px-3 py-1 rounded-full text-[10px] font-bold transition-all active:scale-95 whitespace-nowrap ${assetViewMode === 'assets' ? 'bg-emerald-600 text-white' : 'text-gray-400 hover:text-gray-300'}`}>Asset</button>
                  </div>
                </div>
              </div>

              {(isLargeScreen || isSyncTrayExpanded) && renderSyncWorkflowStatus()}
              {(isLargeScreen || isSyncTrayExpanded) && isHealthPanelExpanded && (
                <div className="border-b border-gray-800 bg-gray-900 shrink-0">
                  <KnowledgeHealthPanel
                    healthStats={healthStats}
                    isExpanded={isHealthPanelExpanded}
                    setIsExpanded={setIsHealthPanelExpanded}
                    onRunHealthCheck={handleRunDuplicateCheck}
                    isHealthChecking={isHealthChecking}
                    onRunAiHealthCheck={handleRunHealthCheck}
                    isAuditing={isAuditing}
                    setActiveTab={setActiveTab}
                    setSelectedAssetFilter={setSelectedAssetFilter}
                    setSelectedNoteFilter={setSelectedNoteFilter}
                    setSinkViewMode={setSinkViewMode}
                    setAssetViewMode={setAssetViewMode}
                    setIsSyncTrayExpanded={setIsSyncTrayExpanded}
                    onOpenPendingSummaries={openChatHistoryPending}
                    setSelectedInsightStatus={setSelectedInsightStatus}
                    setSelectedInsightKind={setSelectedInsightKind}
                    setSelectedInsightType={setSelectedInsightType}
                    setSelectedInsightSourceNoteId={setSelectedInsightSourceNoteId}
                    setSelectedInsightSourceIds={setSelectedInsightSourceIds}
                    setSelectedNoteId={setSelectedNoteId}
                    setSelectedInsightId={setSelectedInsightId}
                    setSelectedAssetId={setSelectedAssetId}
                    setInsightSearchText={setInsightSearchText}
                    setNoteSearchText={setNoteSearchText}
                    setAssetSearchText={setAssetSearchText}
                  />
                </div>
              )}
              <div className="flex-1 flex flex-col min-h-0">
                {assetViewMode === 'notes' ? (
                  <div className="flex-1 flex flex-col min-h-0 bg-gray-900 overflow-hidden">
                    {!selectedNote ? (
                      <NoteList
                        notes={notes}
                        filteredNotes={filteredNotes}
                        setSelectedNoteId={setSelectedNoteId}
                        noteSearchText={noteSearchText}
                        setNoteSearchText={setNoteSearchText}
                        globalSelectedTags={globalSelectedTags}
                        setGlobalSelectedTags={setGlobalSelectedTags}
                        selectedNoteFilter={selectedNoteFilter}
                        setSelectedNoteFilter={setSelectedNoteFilter}
                        allAvailableTags={allAvailableTags}
                        notesForSelectedTag={notesForSelectedTag}
                        handleExtractInsightsByTag={handleExtractInsightsByTag}
                        handleReSummarize={handleReSummarize}
                        isSummarizing={isSummarizing}
                        isCrystallizing={isCrystallizing}
                        insights={insights}
                        jumpToMap={jumpToMap}
                        isLargeScreen={isLargeScreen}
                        handleDiscussInSink={handleDiscussInSink}
                        handleUpdateNoteTags={handleUpdateNoteTags}
                        tagSuggestions={tagSuggestions}
                        showToast={showToast}
                      />
                    ) : (
                      <NoteDetail
                        selectedNote={selectedNote}
                        setSelectedNoteId={setSelectedNoteId}
                        handleCrystallizeNote={handleCrystallizeNote}
                        handleReSummarize={handleReSummarize}
                        isSummarizing={isSummarizing}
                        isCrystallizing={isCrystallizing}
                        selectedNoteInsightCount={selectedNoteInsightCount}
                        setSelectedInsightSourceNoteId={setSelectedInsightSourceNoteId}
                        setSelectedInsightType={setSelectedInsightType}
                        setSelectedInsightStatus={setSelectedInsightStatus}
                        setInsightSearchText={setInsightSearchText}
                        openSyncWorkspace={openSyncWorkspace}
                        handleUpdateNoteTags={handleUpdateNoteTags}
                        openRawSource={openRawSource}
                        jumpToMap={jumpToMap}
                        handleDiscussInSink={handleDiscussInSink}
                        insights={insights}
                        concepts={concepts}
                        setSelectedInsightId={setSelectedInsightId}
                        setSelectedAssetId={setSelectedAssetId}
                        tagSuggestions={tagSuggestions}
                        showToast={showToast}
                      />
                    )}
                  </div>
                ) : assetViewMode === 'insights' ? (
                  <div className="flex-1 flex flex-col min-h-0 bg-gray-900 overflow-hidden">
                      {!selectedInsight ? (
                        <InsightList
                          insights={insights}
                          filteredInsights={filteredInsights}
                          insightSearchText={insightSearchText}
                          setInsightSearchText={setInsightSearchText}
                          globalSelectedTags={globalSelectedTags}
                          setGlobalSelectedTags={setGlobalSelectedTags}
                          allAvailableTags={allAvailableTags}
                          selectedInsightStatus={selectedInsightStatus}
                          setSelectedInsightStatus={setSelectedInsightStatus}
                          selectedInsightKind={selectedInsightKind}
                          setSelectedInsightKind={setSelectedInsightKind}
                          selectedInsightType={selectedInsightType}
                          setSelectedInsightType={setSelectedInsightType}
                          selectedInsightSourceNoteId={selectedInsightSourceNoteId}
                          setSelectedInsightSourceNoteId={setSelectedInsightSourceNoteId}
                          selectedInsightSourceIds={selectedInsightSourceIds}
                          setSelectedInsightSourceIds={setSelectedInsightSourceIds}
                          notes={notes}
                          setSelectedInsightId={setSelectedInsightId}
                          jumpToMap={jumpToMap}
                          isLargeScreen={isLargeScreen}
                          concepts={concepts}
                          onSelectAsset={(id) => {
                            setSelectedAssetId(id);
                            setAssetViewMode('assets');
                          }}
                        />
                      ) : (
                        <InsightDetail
                          selectedInsight={selectedInsight}
                          handleAcceptDuplicateProposal={handleAcceptDuplicateProposal}
                          isProcessingDuplicate={isProcessingDuplicate}
                          setSelectedInsightId={setSelectedInsightId}
                          openAssetTypeModal={openAssetTypeModal}
                          setRejectingInsightId={setRejectingInsightId}
                          setRejectReason={setRejectReason}
                          setIsRejectModalOpen={setIsRejectModalOpen}
                          handleUpdateInsightTags={handleUpdateInsightTags}
                          handleUpdateInsightTitle={handleUpdateInsightTitle}
                          handleUpdateInsightSummary={handleUpdateInsightSummary}
                          handleUpdateInsightMemo={handleUpdateInsightMemo}
                          handleUpdateInsightPatch={handleUpdateInsightPatch}
                          handleUpdateNoteTags={handleUpdateNoteTags}
                          handleUpdateNoteBody={handleUpdateNoteBody}
                          handleUpdateAssetTags={handleUpdateAssetTags}
                          handleUpdateAssetBody={handleUpdateAssetBody}
                          openSourceNote={openSourceNote}
                          openSourceRoom={openSourceRoom}
                          jumpToMap={jumpToMap}
                          handleDiscussInSink={handleDiscussInSink}
                          onAcceptProposal={handleAcceptProposal}
                          isAcceptingProposal={isAcceptingProposal}
                          tagSuggestions={tagSuggestions}
                          showToast={showToast}
                          onReferenceClick={handleReferenceClick}
                          notes={notes}
                          concepts={concepts}
                          insights={insights}
                          chatRooms={chatRooms}
                        />
                      )}
                  </div>
                ) : (
                  <div className="flex-1 flex flex-col min-h-0 bg-gray-900 overflow-hidden">
                      {!selectedAsset ? (
                        <AssetList
                          concepts={concepts}
                          filteredAssets={filteredAssets}
                          assetSearchText={assetSearchText}
                          setAssetSearchText={setAssetSearchText}
                          globalSelectedTags={globalSelectedTags}
                          setGlobalSelectedTags={setGlobalSelectedTags}
                          allAvailableTags={allAvailableTags}
                          selectedAssetStatus={selectedAssetStatus}
                          setSelectedAssetStatus={setSelectedAssetStatus}
                          setSelectedAssetId={setSelectedAssetId}
                          getAssetTypeLabel={getAssetTypeLabel}
                          jumpToMap={jumpToMap}
                          isLargeScreen={isLargeScreen}
                        />
                      ) : (
                        <AssetDetail
                          selectedAsset={selectedAsset}
                          setSelectedAssetId={setSelectedAssetId}
                          handleUpdateAssetTitle={handleUpdateAssetTitle}
                          handleUpdateAssetBody={handleUpdateAssetBody}
                          handleUpdateAssetType={handleUpdateAssetType}
                          handleUpdateAssetTags={handleUpdateAssetTags}
                          getAssetTypeLabel={getAssetTypeLabel}
                          ASSET_TYPE_DESCRIPTIONS={ASSET_TYPE_DESCRIPTIONS}
                          ASSET_TYPE_LABELS={ASSET_TYPE_LABELS}
                          ASSET_STATUS_LABELS={ASSET_STATUS_LABELS}
                          handleUpdateAssetStatus={handleUpdateAssetStatus}
                          handleUpdateAssetMaturity={handleUpdateAssetMaturity}
                          getAssetStatusLabel={getAssetStatusLabel}
                          jumpToMap={jumpToMap}
                          handleDiscussInSink={handleDiscussInSink}
                          insights={insights}
                          notes={notes}
                          tagSuggestions={tagSuggestions}
                          showToast={showToast}
                          chatRooms={chatRooms}
                          onReferenceClick={handleReferenceClick}
                          openSourceRoom={openSourceRoom}
                        />
                      )}
                  </div>
                )}
              </div>
            </div>
              </div>
            </Panel>
            <PanelResizeHandle className="w-1 bg-gray-700 hover:bg-indigo-500 transition-colors cursor-col-resize" />
            <Panel defaultSize={50} minSize={20}>
              <div className="h-full flex flex-col bg-slate-950 overflow-hidden relative">
            {isGuideOpen && guideStepIndex === 2 && (
              <div className="absolute inset-0 z-[60] pointer-events-none ring-4 ring-indigo-500 ring-inset shadow-[0_0_50px_rgba(99,102,241,0.3)] animate-pulse transition-all duration-500" />
            )}
            
            <div className="flex items-center justify-between px-3 py-2 border-b border-gray-800 bg-slate-900/80 shrink-0 z-20 h-10 transition-all duration-500">
              <div className="flex items-center gap-2">
                <button
                  onClick={handleResetThink}
                  title="表示をリセット"
                  className="p-1 hover:bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors shrink-0"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path></svg>
                </button>
              </div>
              <div className="flex items-center gap-2 ml-2 shrink-0">
                <div className="flex bg-slate-950 rounded-full p-0.5 border border-slate-800 overflow-x-auto no-scrollbar">
                  <button onClick={() => setSyncViewMode('map')} className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold transition-all active:scale-95 whitespace-nowrap ${syncViewMode === 'map' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-slate-300'}`}>Map</button>
                  <button onClick={() => setSyncViewMode('list')} className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold transition-all active:scale-95 whitespace-nowrap ${syncViewMode === 'list' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-slate-300'}`}>List</button>
                </div>
                {isLargeScreen && (
                  <button onClick={openSettings} className="text-slate-400 hover:text-white transition-colors" title="Settings">
                    ⚙️
                  </button>
                )}
              </div>
            </div>
            
            <div className="flex-1 flex flex-col bg-slate-950 relative min-h-0">
              {syncViewMode === 'map' && syncTagsCount > syncTags.items.length && (
                <div className="z-20 text-[10px] text-slate-500 px-4 py-1.5 bg-slate-900/50 border-b border-slate-800 transition-all duration-500">
                  表示中 {syncTags.items.length} / 全 {syncTagsCount} タグ。他にもタグがあります。検索で絞り込めます。
                </div>
              )}
              {syncHistory.length > 1 && (
                <div className="z-20 flex h-9 flex-shrink-0 items-center border-b border-slate-800 bg-slate-950/90 px-3">
                  <div className="flex items-center gap-2 overflow-x-auto no-scrollbar py-1.5">
                    {syncHistory.slice(1).map((crumb, index) => (
                      <span key={crumb.id} className="flex flex-shrink-0 items-center gap-2">
                        {index > 0 && <span className="text-slate-700">/</span>}
                        <button onClick={() => navigateSyncHistory(index + 1)} className={`text-[11px] font-bold whitespace-nowrap ${index + 1 === syncHistory.length - 1 ? 'text-indigo-300' : 'text-slate-500 hover:text-indigo-300'}`}>{crumb.label}</button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {syncViewMode === 'list' ? (
                <ThinkList
                  syncCurrentId={syncCurrentId}
                  syncCurrentNode={syncCurrentNode}
                  syncGraph={syncGraph}
                  handleDrillDown={handleDrillDown}
                  getDisplayEntityType={getDisplayEntityType}
                  handleDiscussThinkNode={handleDiscussThinkNode}
                  setSelectedNoteId={setSelectedNoteId}
                  setSelectedAssetId={setSelectedAssetId}
                  setSelectedInsightId={setSelectedInsightId}
                  openSinkNotes={openSinkNotes}
                  openSyncWorkspace={openSyncWorkspace}
                  setGlobalSelectedTags={setGlobalSelectedTags}
                  setSyncViewMode={setSyncViewMode}
                />
              ) : (
                <ThinkGraph 
                 syncCurrentId={syncCurrentId} 
                 syncFlowNodes={syncFlowNodes} 
                 syncFlowEdges={syncFlowEdges} 
                 syncNodeTypes={syncNodeTypes} 
                 syncGraph={syncGraph} 
                 handleSyncNodeClick={handleSyncNodeClick} 
                 handleDrillDown={handleDrillDown}
                 isLargeScreen={isLargeScreen}
                 setSelectedNoteId={setSelectedNoteId}
                 setSelectedAssetId={setSelectedAssetId}
                 setSelectedInsightId={setSelectedInsightId}
                 openSinkNotes={() => setActiveTab('notes')}
                 openSyncWorkspace={openSyncWorkspace}
                 handleDiscussThinkNode={handleDiscussThinkNode}
                 setGlobalSelectedTags={setGlobalSelectedTags}
                 setSyncViewMode={setSyncViewMode}
                 />
              )}
            </div>

            <div className="shrink-0 z-20 border-t border-slate-800 bg-slate-900/90 px-3 py-3 shadow-2xl">
              <div className="flex flex-col gap-3">
                <div className="flex gap-2 overflow-x-auto no-scrollbar -my-1">
                  {[
                    ['important', '重要'],
                    ['recent', '最近'],
                    ['unprocessed', '未整理'],
                    ['all', 'すべて'],
                  ].map(([mode, label]) => (
                    <button
                      key={mode}
                      onClick={() => setMapFilterMode(mode)}
                      className={`flex-shrink-0 text-xs font-bold rounded-full px-3 py-1 my-1 border transition-all ${
                        mapFilterMode === mode
                          ? 'bg-indigo-600 text-white border-indigo-500 shadow-sm'
                          : 'bg-slate-800 text-slate-400 border-slate-700 hover:border-slate-600'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                <div className="relative flex-1">
                  <input
                    type="text"
                    placeholder="Thinkを検索..."
                    value={mapSearchQuery}
                    onChange={(e) => setMapSearchQuery(e.target.value)}
                    className="w-full bg-slate-800 border border-slate-700 text-white text-sm rounded-xl px-4 py-2.5 pl-10 outline-none focus:border-indigo-500 transition-all placeholder-slate-500 shadow-inner h-[40px]"
                  />
                  <svg className="absolute left-3.5 top-3 h-4 w-4 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  {mapSearchQuery && (
                    <button onClick={() => setMapSearchQuery('')} className="absolute right-3 top-3 text-slate-500 hover:text-slate-300">
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            </div>
              </div>
            </Panel>
          </PanelGroup>
        ) : (
          <>
            {activeTab === 'input' && (
              <div className="absolute inset-0 flex flex-col bg-gray-900 overflow-hidden z-10" style={{ paddingBottom: isKeyboardOpen ? '0px' : 'calc(60px + env(safe-area-inset-bottom))' }}>
                {isGuideOpen && guideStepIndex === 0 && (
                  <div className="absolute inset-0 z-[60] pointer-events-none ring-4 ring-indigo-500 ring-inset shadow-[0_0_50px_rgba(99,102,241,0.3)] animate-pulse-slow transition-all duration-500" />
                )}

                {sinkViewMode !== 'input' && (
                  <div className="flex items-center justify-between p-3 border-b border-gray-800 bg-gray-900/80 shrink-0 z-20 h-12 transition-all duration-500">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-black text-white uppercase tracking-widest">Sink</span>
                    </div>
                    <div className="flex bg-gray-950 rounded-full p-0.5 border border-gray-800 ml-2 overflow-x-auto no-scrollbar shrink-0">
                      <button onClick={() => setInputMode('input')} className={`px-3 py-1 rounded-full text-xs font-bold transition-all active:scale-95 whitespace-nowrap ${inputMode === 'input' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-gray-300'}`}>Sink</button>
                      <button onClick={() => setInputMode('discuss')} className={`px-3 py-1 rounded-full text-xs font-bold transition-all active:scale-95 whitespace-nowrap ${inputMode === 'discuss' ? 'bg-indigo-600 text-white' : 'text-gray-400 hover:text-gray-300'}`}>Discuss</button>
                      <button onClick={() => setInputMode('anti')} className={`px-3 py-1 rounded-full text-xs font-bold transition-all active:scale-95 whitespace-nowrap ${inputMode === 'anti' ? 'bg-rose-600 text-white' : 'text-gray-400 hover:text-gray-300'}`}>Anti</button>
                    </div>
                  </div>
                )}

                {sinkViewMode === 'input' ? (
                  <SinkChatPane
                    sinkMessages={sinkMessages}
                    sinkMessagesEndRef={sinkMessagesEndRef}
                    sinkScrollContainerRef={sinkScrollContainerRef}
                    sinkRoomScrollPositions={sinkRoomScrollPositions}
                    sinkRoomId={sinkRoomId}
                    currentRoom={currentRoom}
                    scrollToBottom={scrollToBottom}
                    highlightedRawMessageIds={highlightedRawMessageIds}
                    concepts={concepts}
                    notes={notes}
                    insights={insights}
                    getAssetTypeLabel={getAssetTypeLabel}
                    onReferenceClick={handleReferenceClick}
                    openMemoModal={openMemoModal}
                    isSummarizing={isSummarizing}
                    isSaving={isSaving}
                    isLargeScreen={isLargeScreen}
                    sinkInputText={sinkInputText}
                    setSinkInputText={handleSinkInputTextChange}
                    sinkDraftMetadata={sinkDraftMetadata}
                    onLongPasteDetected={handleLongPasteDetected}
                    longPasteCharThreshold={LONG_PASTE_CHAR_THRESHOLD}
                    sinkInputCharLimit={SINK_INPUT_CHAR_LIMIT}
                    aiActiveInputCharLimit={AI_ACTIVE_INPUT_CHAR_LIMIT}
                    currentRoomWorkflow={currentRoom?.workflow || null}
                    showFirstUseFlow={showFirstUseFlow}
                    onOpenSummaryNote={handleOpenCurrentSummaryNote}
                    inputMode={inputMode}
                    setInputMode={setInputMode}
                    setHistoryIndex={setHistoryIndex}
                    handleInputKeyDown={handleInputKeyDown}
                    handleSave={handleSave}
                    debugMode={debugMode}
                    handleResend={handleResend}
                    openSettings={openSettings}
                    isSinkRoomNearLimit={isSinkRoomNearLimit}
                    isSinkRoomAtLimit={isSinkRoomAtLimit}
                    MAX_MESSAGES_PER_ROOM={MAX_MESSAGES_PER_ROOM}
                    INPUT_MODE_DETAILS={INPUT_MODE_DETAILS}
                    handleStartNewRoom={handleStartNewRoom}
                    handleSummarizeAndNewRoom={handleSummarizeAndNewRoom}
                    setIsChatListModalOpen={() => openChatHistory()}
                    isDeepDiscussMode={isDeepDiscussMode}
                    setIsDeepDiscussMode={setIsDeepDiscussMode}
                    isMultilineMode={isMultilineMode}
                    setIsMultilineMode={setIsMultilineMode}
                    activeReferencedAssets={activeReferencedAssets}
                    onRemoveAsset={handleRemoveIgnoredAsset}
                    sinkContextTitle={(() => {
                      const room = chatRooms.find(r => r.id === sinkRoomId);
                      if (room?.contextRefs?.length > 0) return room.title;
                      if (pendingSinkContextRefs?.length > 0) return pendingSinkRoomTitle;
                      return null;
                    })()}
                  />
                ) : (
                  <div className="flex-1 flex flex-col min-h-0 bg-gray-900 overflow-hidden">
                    {!selectedNote ? (
                      <NoteList
                        notes={notes}
                        filteredNotes={filteredNotes}
                        setSelectedNoteId={setSelectedNoteId}
                        noteSearchText={noteSearchText}
                        setNoteSearchText={setNoteSearchText}
                        globalSelectedTags={globalSelectedTags}
                        setGlobalSelectedTags={setGlobalSelectedTags}
                        selectedNoteFilter={selectedNoteFilter}
                        setSelectedNoteFilter={setSelectedNoteFilter}
                        allAvailableTags={allAvailableTags}
                        notesForSelectedTag={notesForSelectedTag}
                        handleExtractInsightsByTag={handleExtractInsightsByTag}
                        handleReSummarize={handleReSummarize}
                        isSummarizing={isSummarizing}
                        isCrystallizing={isCrystallizing}
                        insights={insights}
                        handleUpdateNoteTags={handleUpdateNoteTags}
                        tagSuggestions={tagSuggestions}
                        showToast={showToast}
                        jumpToMap={jumpToMap}
                        isLargeScreen={isLargeScreen}
                        handleDiscussInSink={handleDiscussInSink}
                      />
                    ) : (
                      <NoteDetail
                        selectedNote={selectedNote}
                        setSelectedNoteId={setSelectedNoteId}
                        handleCrystallizeNote={handleCrystallizeNote}
                        handleReSummarize={handleReSummarize}
                        isSummarizing={isSummarizing}
                        isCrystallizing={isCrystallizing}
                        selectedNoteInsightCount={selectedNoteInsightCount}
                        setSelectedInsightSourceNoteId={setSelectedInsightSourceNoteId}
                        setSelectedInsightType={setSelectedInsightType}
                        setSelectedInsightStatus={setSelectedInsightStatus}
                        setInsightSearchText={setInsightSearchText}
                        openSyncWorkspace={openSyncWorkspace}
                        handleUpdateNoteTags={handleUpdateNoteTags}
                        openRawSource={openRawSource}
                        jumpToMap={jumpToMap}
                        handleDiscussInSink={handleDiscussInSink}
                        insights={insights}
                        concepts={concepts}
                        setSelectedInsightId={setSelectedInsightId}
                        setSelectedAssetId={setSelectedAssetId}
                        tagSuggestions={tagSuggestions}
                        showToast={showToast}
                      />
                    )}
                  </div>
                )}
              </div>
            )}
            {activeTab === 'sync' && (
              <div className="absolute inset-0 z-10 flex flex-col bg-gray-900 overflow-hidden" style={{ paddingBottom: isKeyboardOpen ? '0px' : 'calc(60px + env(safe-area-inset-bottom))' }}>
                <div className={`flex flex-col border-gray-800 bg-gray-900 transition-colors ${isSyncTrayExpanded ? 'flex-1 overflow-hidden' : 'h-12 shrink-0 overflow-hidden'} relative`}>
                  {isGuideOpen && guideStepIndex === 1 && (
                    <div className="absolute inset-0 z-[60] pointer-events-none ring-4 ring-indigo-500 ring-inset shadow-[0_0_50px_rgba(99,102,241,0.3)] animate-pulse transition-all duration-500" />
                  )}

                  <div className={`flex items-center justify-between px-3 border-b border-gray-800 bg-gray-900/80 shrink-0 z-20 h-12 transition-colors cursor-pointer select-none hover:bg-gray-800/60`} onClick={handleToggleSyncTray}>
                    <div className="flex items-center gap-1">
                      <div className="relative">
                        <button
                          onClick={handleToggleHealthPanel}
                          className={`p-1.5 rounded-lg transition-colors shrink-0 ${isHealthPanelExpanded ? 'bg-gray-800 text-white' : 'text-gray-400 hover:bg-gray-800 hover:text-white'}`}
                          title="ナレッジ状態"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                        </button>
                        {totalHealthIssues > 0 && (
                          <span className="absolute top-0 right-0 bg-rose-600 w-2 h-2 rounded-full pointer-events-none" />
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                      {assetViewMode === 'notes' && !selectedNote && (
                    <div className="relative">
                      <button
                        onClick={(e) => { e.stopPropagation(); setIsNoteStatusFilterOpen(v => !v); }}
                        className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold border transition-all whitespace-nowrap ${
                          selectedNoteFilter !== 'all'
                            ? 'bg-blue-600/20 text-blue-300 border-blue-500/60'
                            : 'bg-gray-800/60 text-gray-400 border-gray-700 hover:text-gray-300'
                        }`}
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
                        </svg>
                        {{'unextracted':'Hint未抽出','extracted':'Hint抽出済','all':'すべて'}[selectedNoteFilter]}
                      </button>
                      {isNoteStatusFilterOpen && (
                        <>
                          <div className="fixed inset-0 z-40" onClick={() => setIsNoteStatusFilterOpen(false)} />
                          <div className="absolute right-0 top-full mt-1.5 bg-gray-800 border border-gray-700 rounded-xl shadow-2xl z-50 overflow-hidden min-w-[100px]">
                            {NOTE_STATUS_FILTER_OPTIONS.map(([filter, label]) => (
                              <button
                                key={filter}
                                onClick={(e) => { e.stopPropagation(); setSelectedNoteFilter(filter); setIsNoteStatusFilterOpen(false); }}
                                className={`w-full text-left px-3 py-2 text-[11px] font-bold transition-colors ${
                                  selectedNoteFilter === filter
                                    ? 'bg-blue-600/30 text-blue-300'
                                    : 'text-gray-300 hover:bg-gray-700'
                                }`}
                              >
                                {label}
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  {assetViewMode === 'insights' && !selectedInsight && (
                        <div className="relative">
                          <button
                            onClick={(e) => { e.stopPropagation(); setIsInsightStatusFilterOpen(v => !v); }}
                            className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold border transition-all whitespace-nowrap ${
                              selectedInsightStatus !== 'all'
                                ? 'bg-purple-600/20 text-purple-300 border-purple-500/60'
                                : 'bg-gray-800/60 text-gray-400 border-gray-700 hover:text-gray-300'
                            }`}
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
                            </svg>
                            {Object.fromEntries(INSIGHT_STATUS_FILTER_OPTIONS)[selectedInsightStatus]}
                          </button>
                          {isInsightStatusFilterOpen && (
                            <>
                              <div className="fixed inset-0 z-40" onClick={() => setIsInsightStatusFilterOpen(false)} />
                              <div className="absolute right-0 top-full mt-1.5 bg-gray-800 border border-gray-700 rounded-xl shadow-2xl z-50 overflow-hidden min-w-[100px]">
                                {INSIGHT_STATUS_FILTER_OPTIONS.map(([status, label]) => (
                                  <button
                                    key={status}
                                    onClick={(e) => { e.stopPropagation(); setSelectedInsightStatus(status); setSelectedInsightType('all'); setIsInsightStatusFilterOpen(false); }}
                                    className={`w-full text-left px-3 py-2 text-[11px] font-bold transition-colors ${
                                      selectedInsightStatus === status
                                        ? 'bg-purple-600/30 text-purple-300'
                                        : 'text-gray-300 hover:bg-gray-700'
                                    }`}
                                  >
                                    {label}
                                  </button>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      )}
                      <div className="flex bg-gray-950 rounded-full p-0.5 border border-gray-800 overflow-x-auto no-scrollbar shrink-0">
                        <button onClick={(e) => { e.stopPropagation(); setSelectedNoteId(null); setSelectedInsightId(null); setSelectedAssetId(null); setAssetViewMode('notes'); setIsSyncTrayExpanded(true); }} className={`px-3 py-1 rounded-full text-[10px] font-bold transition-all active:scale-95 whitespace-nowrap ${assetViewMode === 'notes' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-gray-300'}`}>Note</button>
                        <button onClick={(e) => { e.stopPropagation(); setSelectedNoteId(null); setSelectedInsightId(null); setSelectedAssetId(null); setAssetViewMode('insights'); setIsSyncTrayExpanded(true); }} className={`px-3 py-1 rounded-full text-[10px] font-bold transition-all active:scale-95 whitespace-nowrap ${assetViewMode === 'insights' ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-gray-300'}`}>Hint</button>
                        <button onClick={(e) => { e.stopPropagation(); setSelectedNoteId(null); setSelectedInsightId(null); setSelectedAssetId(null); setAssetViewMode('assets'); setIsSyncTrayExpanded(true); }} className={`px-3 py-1 rounded-full text-[10px] font-bold transition-all active:scale-95 whitespace-nowrap ${assetViewMode === 'assets' ? 'bg-emerald-600 text-white' : 'text-gray-400 hover:text-gray-300'}`}>Asset</button>
                      </div>
                    </div>
                  </div>

                  {isSyncTrayExpanded && renderSyncWorkflowStatus()}
                  {isSyncTrayExpanded && isHealthPanelExpanded && (
                    <div className="border-b border-gray-800 bg-gray-900 shrink-0">
                      <KnowledgeHealthPanel
                        healthStats={healthStats}
                        isExpanded={isHealthPanelExpanded}
                        setIsExpanded={setIsHealthPanelExpanded}
                        onRunHealthCheck={handleRunDuplicateCheck}
                        isHealthChecking={isHealthChecking}
                        onRunAiHealthCheck={handleRunHealthCheck}
                        isAuditing={isAuditing}
                        setActiveTab={setActiveTab}
                        setSelectedAssetFilter={setSelectedAssetFilter}
                        setSelectedNoteFilter={setSelectedNoteFilter}
                        setSinkViewMode={setSinkViewMode}
                        setAssetViewMode={setAssetViewMode}
                        setIsSyncTrayExpanded={setIsSyncTrayExpanded}
                        onOpenPendingSummaries={openChatHistoryPending}
                        setSelectedInsightStatus={setSelectedInsightStatus}
                        setSelectedInsightKind={setSelectedInsightKind}
                        setSelectedInsightType={setSelectedInsightType}
                        setSelectedInsightSourceNoteId={setSelectedInsightSourceNoteId}
                        setSelectedInsightSourceIds={setSelectedInsightSourceIds}
                        setSelectedNoteId={setSelectedNoteId}
                        setSelectedInsightId={setSelectedInsightId}
                        setSelectedAssetId={setSelectedAssetId}
                        setInsightSearchText={setInsightSearchText}
                        setNoteSearchText={setNoteSearchText}
                        setAssetSearchText={setAssetSearchText}
                      />
                    </div>
                  )}
                  <div className="flex-1 flex flex-col min-h-0">
                    {assetViewMode === 'notes' ? (
                      <div className="flex-1 flex flex-col min-h-0 bg-gray-900 overflow-hidden">
                        {!selectedNote ? (
                          <NoteList
                            notes={notes}
                            filteredNotes={filteredNotes}
                            setSelectedNoteId={setSelectedNoteId}
                            noteSearchText={noteSearchText}
                            setNoteSearchText={setNoteSearchText}
                            globalSelectedTags={globalSelectedTags}
                            setGlobalSelectedTags={setGlobalSelectedTags}
                            selectedNoteFilter={selectedNoteFilter}
                            setSelectedNoteFilter={setSelectedNoteFilter}
                            allAvailableTags={allAvailableTags}
                            notesForSelectedTag={notesForSelectedTag}
                            handleExtractInsightsByTag={handleExtractInsightsByTag}
                            handleReSummarize={handleReSummarize}
                            isSummarizing={isSummarizing}
                            isCrystallizing={isCrystallizing}
                            insights={insights}
                            jumpToMap={jumpToMap}
                            isLargeScreen={isLargeScreen}
                            handleDiscussInSink={handleDiscussInSink}
                            handleUpdateNoteTags={handleUpdateNoteTags}
                            tagSuggestions={tagSuggestions}
                            showToast={showToast}
                          />
                        ) : (
                          <NoteDetail
                            selectedNote={selectedNote}
                            setSelectedNoteId={setSelectedNoteId}
                            handleCrystallizeNote={handleCrystallizeNote}
                            handleReSummarize={handleReSummarize}
                            isSummarizing={isSummarizing}
                            isCrystallizing={isCrystallizing}
                            selectedNoteInsightCount={selectedNoteInsightCount}
                            setSelectedInsightSourceNoteId={setSelectedInsightSourceNoteId}
                            setSelectedInsightType={setSelectedInsightType}
                            setInsightSearchText={setInsightSearchText}
                            openSyncWorkspace={openSyncWorkspace}
                            handleUpdateNoteTags={handleUpdateNoteTags}
                            openRawSource={openRawSource}
                            jumpToMap={jumpToMap}
                            handleDiscussInSink={handleDiscussInSink}
                            insights={insights}
                            concepts={concepts}
                            setSelectedInsightId={setSelectedInsightId}
                            setSelectedAssetId={setSelectedAssetId}
                            tagSuggestions={tagSuggestions}
                            showToast={showToast}
                          />
                        )}
                      </div>
                    ) : assetViewMode === 'insights' ? (
                      <div className="flex-1 flex flex-col min-h-0 bg-gray-900 overflow-hidden">
                          {!selectedInsight ? (
                            <InsightList
                              insights={insights}
                              filteredInsights={filteredInsights}
                              insightSearchText={insightSearchText}
                              setInsightSearchText={setInsightSearchText}
                              globalSelectedTags={globalSelectedTags}
                              setGlobalSelectedTags={setGlobalSelectedTags}
                              allAvailableTags={allAvailableTags}
                              selectedInsightStatus={selectedInsightStatus}
                              setSelectedInsightStatus={setSelectedInsightStatus}
                              selectedInsightKind={selectedInsightKind}
                              setSelectedInsightKind={setSelectedInsightKind}
                              selectedInsightType={selectedInsightType}
                              setSelectedInsightType={setSelectedInsightType}
                              selectedInsightSourceNoteId={selectedInsightSourceNoteId}
                              setSelectedInsightSourceNoteId={setSelectedInsightSourceNoteId}
                              selectedInsightSourceIds={selectedInsightSourceIds}
                              setSelectedInsightSourceIds={setSelectedInsightSourceIds}
                              notes={notes}
                              setSelectedInsightId={setSelectedInsightId}
                              jumpToMap={jumpToMap}
                              isLargeScreen={isLargeScreen}
                              concepts={concepts}
                              onSelectAsset={(id) => {
                                setSelectedAssetId(id);
                                setAssetViewMode('assets');
                              }}
                            />
                          ) : (
                            <InsightDetail
                              selectedInsight={selectedInsight}
                              handleAcceptDuplicateProposal={handleAcceptDuplicateProposal}
                              isProcessingDuplicate={isProcessingDuplicate}
                              setSelectedInsightId={setSelectedInsightId}
                              openAssetTypeModal={openAssetTypeModal}
                              setRejectingInsightId={setRejectingInsightId}
                              setRejectReason={setRejectReason}
                              setIsRejectModalOpen={setIsRejectModalOpen}
                              handleUpdateInsightTags={handleUpdateInsightTags}
                              handleUpdateInsightTitle={handleUpdateInsightTitle}
                              handleUpdateInsightSummary={handleUpdateInsightSummary}
                              handleUpdateInsightMemo={handleUpdateInsightMemo}
                              handleUpdateInsightPatch={handleUpdateInsightPatch}
                              handleUpdateNoteTags={handleUpdateNoteTags}
                              handleUpdateNoteBody={handleUpdateNoteBody}
                              handleUpdateAssetTags={handleUpdateAssetTags}
                              handleUpdateAssetBody={handleUpdateAssetBody}
                              openSourceNote={openSourceNote}
                              openSourceRoom={openSourceRoom}
                              jumpToMap={jumpToMap}
                              handleDiscussInSink={handleDiscussInSink}
                              onAcceptProposal={handleAcceptProposal}
                              isAcceptingProposal={isAcceptingProposal}
                              tagSuggestions={tagSuggestions}
                              showToast={showToast}
                              onReferenceClick={handleReferenceClick}
                              notes={notes}
                              concepts={concepts}
                              insights={insights}
                              chatRooms={chatRoomsWithWorkflow}
                            />
                          )}
                      </div>
                    ) : (
                      <div className="flex-1 flex flex-col min-h-0 bg-gray-900 overflow-hidden">
                          {!selectedAsset ? (
                            <AssetList
                              concepts={concepts}
                              filteredAssets={filteredAssets}
                              assetSearchText={assetSearchText}
                              setAssetSearchText={setAssetSearchText}
                              globalSelectedTags={globalSelectedTags}
                              setGlobalSelectedTags={setGlobalSelectedTags}
                              allAvailableTags={allAvailableTags}
                              selectedAssetStatus={selectedAssetStatus}
                              setSelectedAssetStatus={setSelectedAssetStatus}
                              setSelectedAssetId={setSelectedAssetId}
                              getAssetTypeLabel={getAssetTypeLabel}
                              jumpToMap={jumpToMap}
                              isLargeScreen={isLargeScreen}
                              selectedAssetFilter={selectedAssetFilter}
                              setSelectedAssetFilter={setSelectedAssetFilter}
                            />
                          ) : (
                            <AssetDetail
                              selectedAsset={selectedAsset}
                              setSelectedAssetId={setSelectedAssetId}
                              handleUpdateAssetTitle={handleUpdateAssetTitle}
                              handleUpdateAssetBody={handleUpdateAssetBody}
                              handleUpdateAssetType={handleUpdateAssetType}
                              handleUpdateAssetTags={handleUpdateAssetTags}
                              getAssetTypeLabel={getAssetTypeLabel}
                              ASSET_TYPE_DESCRIPTIONS={ASSET_TYPE_DESCRIPTIONS}
                              ASSET_TYPE_LABELS={ASSET_TYPE_LABELS}
                              ASSET_STATUS_LABELS={ASSET_STATUS_LABELS}
                              handleUpdateAssetStatus={handleUpdateAssetStatus}
                              handleUpdateAssetMaturity={handleUpdateAssetMaturity}
                              getAssetStatusLabel={getAssetStatusLabel}
                              jumpToMap={jumpToMap}
                              handleDiscussInSink={handleDiscussInSink}
                              insights={insights}
                              notes={notes}
                              tagSuggestions={tagSuggestions}
                              showToast={showToast}
                              chatRooms={chatRooms}
                              onReferenceClick={handleReferenceClick}
                              openSourceRoom={openSourceRoom}
                            />
                          )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            {activeTab === 'think' && (
              <div className="absolute inset-0 z-10 flex flex-col bg-slate-950 overflow-hidden" style={{ paddingBottom: isKeyboardOpen ? '0px' : 'calc(60px + env(safe-area-inset-bottom))' }}>
                {isGuideOpen && guideStepIndex === 2 && (
                  <div className="absolute inset-0 z-[60] pointer-events-none ring-4 ring-indigo-500 ring-inset shadow-[0_0_50px_rgba(99,102,241,0.3)] animate-pulse transition-all duration-500" />
                )}

                <div className="flex items-center justify-between px-3 py-2 border-b border-gray-800 bg-slate-900/80 shrink-0 z-20 h-10 transition-all duration-500">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleResetThink}
                      title="表示をリセット"
                      className="p-1 hover:bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors shrink-0"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path></svg>
                    </button>
                  </div>
                  <div className="flex items-center gap-2 ml-2 shrink-0">
                    <div className="flex bg-slate-950 rounded-full p-0.5 border border-slate-800 overflow-x-auto no-scrollbar">
                      <button onClick={() => setSyncViewMode('map')} className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold transition-all active:scale-95 whitespace-nowrap ${syncViewMode === 'map' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-slate-300'}`}>Map</button>
                      <button onClick={() => setSyncViewMode('list')} className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold transition-all active:scale-95 whitespace-nowrap ${syncViewMode === 'list' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-slate-300'}`}>List</button>
                    </div>
                  </div>
                </div>

                <div className="flex-1 flex flex-col bg-slate-950 relative min-h-0">
                  {syncViewMode === 'map' && syncTagsCount > syncTags.items.length && (
                    <div className="z-20 text-[10px] text-slate-500 px-4 py-1.5 bg-slate-900/50 border-b border-slate-800 transition-all duration-500">
                      表示中 {syncTags.items.length} / 全 {syncTagsCount} タグ。他にもタグがあります。検索で絞り込めます。
                    </div>
                  )}
                  {syncHistory.length > 1 && (
                    <div className="z-20 flex h-9 flex-shrink-0 items-center border-b border-slate-800 bg-slate-950/90 px-3">
                      <div className="flex items-center gap-2 overflow-x-auto no-scrollbar py-1.5">
                        {syncHistory.slice(1).map((crumb, index) => (
                          <span key={crumb.id} className="flex flex-shrink-0 items-center gap-2">
                            {index > 0 && <span className="text-slate-700">/</span>}
                            <button onClick={() => navigateSyncHistory(index + 1)} className={`text-[11px] font-bold whitespace-nowrap ${index + 1 === syncHistory.length - 1 ? 'text-indigo-300' : 'text-slate-500 hover:text-indigo-300'}`}>{crumb.label}</button>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {syncViewMode === 'list' ? (
                    <ThinkList
                      syncCurrentId={syncCurrentId}
                      syncCurrentNode={syncCurrentNode}
                      syncGraph={syncGraph}
                      handleDrillDown={handleDrillDown}
                      getDisplayEntityType={getDisplayEntityType}
                      handleDiscussThinkNode={handleDiscussThinkNode}
                      setSelectedNoteId={setSelectedNoteId}
                      setSelectedAssetId={setSelectedAssetId}
                      setSelectedInsightId={setSelectedInsightId}
                      openSinkNotes={openSinkNotes}
                      openSyncWorkspace={openSyncWorkspace}
                      setGlobalSelectedTags={setGlobalSelectedTags}
                      setSyncViewMode={setSyncViewMode}
                    />
                  ) : (
                    <ThinkGraph
                     syncCurrentId={syncCurrentId}
                     syncFlowNodes={syncFlowNodes}
                     syncFlowEdges={syncFlowEdges}
                     syncNodeTypes={syncNodeTypes}
                     syncGraph={syncGraph}
                     handleSyncNodeClick={handleSyncNodeClick}
                     handleDrillDown={handleDrillDown}
                     isLargeScreen={isLargeScreen}
                     setSelectedNoteId={setSelectedNoteId}
                     setSelectedAssetId={setSelectedAssetId}
                     setSelectedInsightId={setSelectedInsightId}
                     openSinkNotes={openSinkNotes}
                     openSyncWorkspace={openSyncWorkspace}
                     handleDiscussThinkNode={handleDiscussThinkNode}
                     setGlobalSelectedTags={setGlobalSelectedTags}
                     setSyncViewMode={setSyncViewMode}
                     />
                  )}
                </div>

                <div className="shrink-0 z-20 border-t border-slate-800 bg-slate-900/90 px-3 py-3 shadow-2xl">
                  <div className="flex flex-col gap-3">
                    <div className="flex gap-2 overflow-x-auto no-scrollbar -my-1">
                      {[
                        ['important', '重要'],
                        ['recent', '最近'],
                        ['unprocessed', '未整理'],
                        ['all', 'すべて'],
                      ].map(([mode, label]) => (
                        <button
                          key={mode}
                          onClick={() => setMapFilterMode(mode)}
                          className={`flex-shrink-0 text-xs font-bold rounded-full px-3 py-1 my-1 border transition-all ${
                            mapFilterMode === mode
                              ? 'bg-indigo-600 text-white border-indigo-500 shadow-sm'
                              : 'bg-slate-800 text-slate-400 border-slate-700 hover:border-slate-600'
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>

                    <div className="relative flex-1">
                      <input
                        type="text"
                        placeholder="Thinkを検索..."
                        value={mapSearchQuery}
                        onChange={(e) => setMapSearchQuery(e.target.value)}
                        className="w-full bg-slate-800 border border-slate-700 text-white text-sm rounded-xl px-4 py-2.5 pl-10 outline-none focus:border-indigo-500 transition-all placeholder-slate-500 shadow-inner h-[40px]"
                      />
                      <svg className="absolute left-3.5 top-3 h-4 w-4 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      {mapSearchQuery && (
                        <button onClick={() => setMapSearchQuery('')} className="absolute right-3 top-3 text-slate-500 hover:text-slate-300">
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
            {activeTab === 'settings' && (
              <div className="absolute inset-0 z-10 flex flex-col bg-gray-900 overflow-hidden" style={{ paddingBottom: isKeyboardOpen ? '0px' : 'calc(60px + env(safe-area-inset-bottom))' }}>
                <div className="flex items-center p-3 border-b border-gray-800 bg-gray-900/80 shrink-0 h-12">
                  <span className="text-xs font-black text-white uppercase tracking-widest">Settings</span>
                </div>
                <SettingsPage
                  isActive={true}
                  handleSaveSettings={handleSaveSettings}
                  user={user}
                  signOut={signOut}
                  llmProvider={llmProvider}
                  setLlmProvider={setLlmProvider}
                  llmModel={llmModel}
                  setLlmModel={setLlmModel}
                  llmModelLight={llmModelLight}
                  setLlmModelLight={setLlmModelLight}
                  llmModelHeavy={llmModelHeavy}
                  setLlmModelHeavy={setLlmModelHeavy}
                  openaiApiKey={openaiApiKey}
                  setOpenaiApiKey={setOpenaiApiKey}
                  anthropicApiKey={anthropicApiKey}
                  setAnthropicApiKey={setAnthropicApiKey}
                  geminiApiKey={geminiApiKey}
                  setGeminiApiKey={setGeminiApiKey}
                  debugMode={debugMode}
                  setDebugMode={setDebugMode}
                  userData={userData}
                  isByok={isByok}
                />
              </div>
            )}
          </>
        )}
      </main>

      <Guide
        isGuideOpen={isGuideOpen}
        setIsGuideOpen={setIsGuideOpen}
        guideStepIndex={guideStepIndex}
        setGuideStepIndex={setGuideStepIndex}
        demoSteps={demoSteps}
        currentGuideStep={currentGuideStep}
        nextGuideStep={nextGuideStep}
        activeTab={activeTab}
        sinkViewMode={sinkViewMode}
        isLargeScreen={isLargeScreen}
      />

      {(!isKeyboardOpen) && (<footer className="lg:hidden fixed bottom-0 left-0 right-0 bg-gray-900 border-t border-gray-800 z-[100] flex justify-around items-center h-[calc(60px+env(safe-area-inset-bottom))] px-2 pb-[env(safe-area-inset-bottom)]">
        <button onClick={openSinkInput} className={`flex-1 flex flex-col items-center justify-center transition-all active:scale-95 ${activeTab === 'input' ? 'text-blue-500' : 'text-gray-400'}`}>
          <span className="text-xl mb-1">💬</span><span className="text-[10px] font-medium">Sink</span>
        </button>
        <button onClick={() => { setSelectedNoteId(null); setSelectedInsightId(null); setSelectedAssetId(null); openSyncWorkspace('insights'); }} className={`flex-1 flex flex-col items-center justify-center transition-all active:scale-95 ${activeTab === 'sync' ? 'text-blue-500' : 'text-gray-400'}`}>
          <div className="relative mb-1">
            <span className="text-xl">🔗</span>
            {totalHealthIssues > 0 && (
              <span className="absolute top-0 -right-1 bg-rose-600 w-2 h-2 rounded-full pointer-events-none" />
            )}
          </div>
          <span className="text-[10px] font-medium">Sync</span>
        </button>
        <button onClick={openThinkWorkspace} className={`flex-1 flex flex-col items-center justify-center transition-all active:scale-95 ${activeTab === 'think' ? 'text-blue-500' : 'text-gray-400'}`}>
          <span className="text-xl mb-1">🧠</span><span className="text-[10px] font-medium">Think</span>
        </button>
        <button onClick={openSettings} className={`flex-1 flex flex-col items-center justify-center transition-all active:scale-95 ${(isLargeScreen ? isSettingsModalOpen : activeTab === 'settings') ? 'text-blue-500' : 'text-gray-400'}`}>
          <span className="text-xl mb-1">⚙️</span><span className="text-[10px] font-medium">Settings</span>
        </button>
      </footer>)}

      <SettingsModal
        isSettingsOpen={isSettingsModalOpen}
        setIsSettingsOpen={setIsSettingsModalOpen}
        handleSaveSettings={handleSaveSettings}
        user={user}
        signOut={signOut}
        llmProvider={llmProvider}
        setLlmProvider={setLlmProvider}
        llmModel={llmModel}
        setLlmModel={setLlmModel}
        llmModelLight={llmModelLight}
        setLlmModelLight={setLlmModelLight}
        llmModelHeavy={llmModelHeavy}
        setLlmModelHeavy={setLlmModelHeavy}
        openaiApiKey={openaiApiKey}
        setOpenaiApiKey={setOpenaiApiKey}
        anthropicApiKey={anthropicApiKey}
        setAnthropicApiKey={setAnthropicApiKey}
        geminiApiKey={geminiApiKey}
        setGeminiApiKey={setGeminiApiKey}
        debugMode={debugMode}
        setDebugMode={setDebugMode}
        userData={userData}
        isByok={isByok}
        onRequestCredits={handleRequestCredits}
      />
      <RejectModal isRejectModalOpen={isRejectModalOpen} setIsRejectModalOpen={setIsRejectModalOpen} handleRejectInsight={handleRejectInsight} rejectingInsightId={rejectingInsightId} insights={insights} rejectReason={rejectReason} setRejectReason={setRejectReason} />
      <MemoModal
        isMemoModalOpen={isMemoModalOpen}
        setIsMemoModalOpen={setIsMemoModalOpen}
        memoSaveMode={memoSaveMode}
        setMemoSaveMode={setMemoSaveMode}
        memoHint={memoHint}
        setMemoHint={setMemoHint}
        handleRunMemoization={handleRunMemoization}
        isSummarizing={isSummarizing}
        existingSummaryNote={existingSummaryNote}
      />
      <NoteCreateModal isNoteCreateModalOpen={isNoteCreateModalOpen} setIsNoteCreateModalOpen={setIsNoteCreateModalOpen} onSubmit={onSubmitManualNote} />
      <AssetTypeModal
        isAssetTypeModalOpen={isAssetTypeModalOpen}
        setIsAssetTypeModalOpen={setIsAssetTypeModalOpen}
        assetizingInsight={assetizingInsight}
        assetTypeDraft={assetTypeDraft}
        setAssetTypeDraft={setAssetTypeDraft}
        assetTitleDraft={assetTitleDraft}
        setAssetTitleDraft={setAssetTitleDraft}
        assetBodyDraft={assetBodyDraft}
        setAssetBodyDraft={setAssetBodyDraft}
        ASSET_TYPES={ASSET_TYPES}
        getAssetTypeLabel={getAssetTypeLabel}
        getAssetStatusLabel={getAssetStatusLabel}
        getInitialAssetStatus={getInitialAssetStatus}
        ASSET_TYPE_DESCRIPTIONS={ASSET_TYPE_DESCRIPTIONS}
        handlePromoteInsightToConcept={handlePromoteInsightToConcept}
        assetDuplicateCandidates={assetDuplicateCandidates}
        handleMergeInsightIntoAsset={handleMergeInsightIntoAsset}
      />
      {isChatListModalOpen && (
        <ChatListModal
          isChatListModalOpen={isChatListModalOpen}
          setIsChatListModalOpen={(val) => {
            setIsChatListModalOpen(val);
            if (!val) setChatListStatusFilter(null);
          }}
          statusFilter={chatListStatusFilter}
          onClearStatusFilter={() => setChatListStatusFilter(null)}
          chatRooms={chatRooms}
          activeRoomId={sinkRoomId}
          onSelectRoom={(id) => {
            const storageKey = `secondBrain.${user?.uid}.sinkRoomId`;
            localStorage.setItem(storageKey, id);
            // Clear scroll position to force scroll to bottom when switching from list
            if (sinkRoomScrollPositions.current) {
              delete sinkRoomScrollPositions.current[id];
            }
            setSinkRoomId(id);
            openSinkInput();
          }}
          onDeleteRoom={handleDeleteRoom}
          onUpdateRoomTitle={handleUpdateRoomTitle}
          onStartNewRoom={handleStartNewRoom}
        />
      )}
      <NewChatConfirmModal isNewChatConfirmOpen={isNewChatConfirmOpen} setIsNewChatConfirmOpen={setIsNewChatConfirmOpen} handleConfirmNewChat={handleConfirmNewChat} />
      <CreditLimitModal
        isOpen={isCreditLimitModalOpen}
        onClose={() => setIsCreditLimitModalOpen(false)}
        message={creditLimitMessage}
        onRequestCredits={handleRequestCredits}
      />
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {showPWAGuide && !isLargeScreen && surface === 'app' && (
        <div className="fixed bottom-[calc(70px+env(safe-area-inset-bottom))] left-4 right-4 z-[160] animate-in slide-in-from-bottom duration-500">
          <div className="bg-gray-800 border border-gray-700 rounded-2xl p-4 shadow-2xl relative">
            <button
              onClick={() => {
                setShowPWAGuide(false);
                localStorage.setItem('sink.pwaGuideDismissed', 'true');
              }}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-300 p-1"
              aria-label="閉じる"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
            <div className="flex items-start gap-3">
              <div className="text-2xl pt-0.5">📲</div>
              <div>
                <p className="text-sm font-bold text-white mb-1">このSinkをホーム画面に追加できます</p>
                <div className="text-[11px] text-gray-400 leading-relaxed">
                  <p>Safariの <span className="inline-block bg-gray-700 rounded px-1 text-gray-200">共有ボタン</span> → <span className="inline-block bg-gray-700 rounded px-1 text-gray-200">ホーム画面に追加</span> → <span className="text-indigo-400 font-bold">追加</span> の手順ですぐに起動できるようになります。</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
